import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Trash2, Save, X as XIcon, AlertTriangle, Printer, Plus, CreditCard, Camera, Trash, ArrowUpCircle, ArrowDownCircle, RefreshCw, PenSquare, Banknote, Truck, Info, Ban, Search, Download, ArrowUp, ArrowDown, Calendar, Mail, FlaskConical, ShieldAlert, CalendarX, DollarSign, ChevronDown, CheckCircle2, Lock, Flag, FlagOff, ImagePlus, Tag, LayoutList } from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { ConversationPanel } from "@/components/admin/ConversationPanel";
import { NewBookingModal } from "@/pages/admin/NewBookingModal";
import { API, BACKEND_URL } from "@/config";
import { compressFiles } from "@/utils/compressImage";
import { formatSlot } from "@/utils/slotLabel";
import { fmtErr } from "@/context/AuthProvider";
import { AgreementsPanel } from "@/components/admin/AgreementsPanel";
import { CommunicationLog } from "@/components/admin/CommunicationLog";
import { LocationPicker } from "@/components/booking/tripsetup/LocationPicker";
import { ThemedSelect } from "@/components/ui/ThemedSelect";

const moneyD = (dollars) => `$${Number(dollars || 0).toFixed(2)}`;

// Date-key selector — matches BabyQuip's radio group so admin can filter the
// same range against purchase / rental-start / rental-end.
const DATE_KEYS = [
  { id: "purchase", label: "Purchase Date", field: "created_at" },
  { id: "rental_start", label: "Rental Start", field: "delivery_date" },
  { id: "rental_end", label: "Rental End", field: "pickup_date" },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

// ---- Order start / late helpers -------------------------------------------
// An order is "started" once the gear is out with the guest (driver deliver
// flow or the admin Start button). Late = paid, not started, and past the
// selected delivery window (Orlando / Eastern time).
const STARTED_STATUSES = ["assigned", "in_progress", "completed"];
const hasStarted = (r) => !!r.delivered_at || STARTED_STATUSES.includes(r.status);
const nowInET = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(new Date());
  const g = (t) => parts.find((p) => p.type === t)?.value || "00";
  return { date: `${g("year")}-${g("month")}-${g("day")}`, minutes: (Number(g("hour")) % 24) * 60 + Number(g("minute")) };
};
const isLateStart = (r) => {
  if (r.payment_status !== "paid" || r.status === "cancelled" || hasStarted(r)) return false;
  if (!r.delivery_date) return false;
  const { date, minutes } = nowInET();
  if (r.delivery_date < date) return true;
  if (r.delivery_date > date) return false;
  const end = parseInt(String(r.delivery_slot || "").split("-")[1], 10);
  return Number.isNaN(end) ? false : minutes >= end * 60;
};
const fmtWhenET = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
};

const StatusChip = ({ payment_status, status, late }) => {
  const cancelled = status === "cancelled";
  const paid = payment_status === "paid" && !cancelled;
  const label = cancelled ? "Cancelled" : late ? "Late — not started" : paid ? "Paid" : "Unpaid";
  const cls = cancelled
    ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
    : late
      ? "bg-red-500/20 text-red-300 border-red-500/60"
      : paid
        ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/50"
        : "bg-amber-500/15 text-amber-300 border-amber-500/40";
  return (
    <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${cls}`}>
      {label}
    </span>
  );
};

// Sortable table-header cell. Clicking cycles asc/desc for the given key.
const SortHeader = ({ label, sortKey, sort, setSort, align = "left" }) => {
  const active = sort.key === sortKey;
  const dir = active ? sort.dir : null;
  return (
    <button
      type="button"
      onClick={() => setSort((s) => ({ key: sortKey, dir: s.key === sortKey && s.dir === "asc" ? "desc" : "asc" }))}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] ${active ? "text-[#FFD166]" : "text-[#B9C8D5] hover:text-[#F6F1E4]"} ${align === "right" ? "ml-auto" : ""}`}
      data-testid={`res-sort-${sortKey}`}
    >
      {label}
      {dir === "asc" ? <ArrowUp className="w-3 h-3" /> : dir === "desc" ? <ArrowDown className="w-3 h-3" /> : null}
    </button>
  );
};

export const Reservations = () => {
  const [items, setItems] = useState([]);
  const [messageTarget, setMessageTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [extraChargeTarget, setExtraChargeTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [productsById, setProductsById] = useState({});

  // ---- filter state (BabyQuip-parity toolbar) ----
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "unpaid" ? "unpaid" : "paid");
  const [query, setQuery] = useState("");
  const [dateKey, setDateKey] = useState("purchase");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const load = useCallback(() => axios.get(`${API}/admin/reservations`).then(r => setItems(r.data.reservations)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/products`).then(r => {
      const map = {};
      (r.data.products || []).forEach(p => { map[p.id] = p; });
      setProductsById(map);
    }).catch(() => {});
  }, []);

  // Deep-link support: /admin/reservations?open=<id> opens the detail drawer
  // straight from a dashboard "Up Next" row click.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || items.length === 0) return;
    const r = items.find((x) => x.id === openId);
    if (r) setDetailTarget(r);
  }, [searchParams, items]);

  const markReturned = async (id) => {
    try { await axios.post(`${API}/admin/reservations/${id}/return`); toast.success("Marked returned"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    const isLivePaid = r.payment_status === "paid" && r.status !== "cancelled";
    if (isLivePaid) return toast.error("Cancel & refund this first, then delete.");
    const label = r.status === "cancelled" ? `cancelled reservation for ${r.customer_name}` : `unpaid reservation for ${r.customer_name}`;
    if (!window.confirm(`Delete the ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/reservations/${r.id}${r.status === "cancelled" ? "?force=true" : ""}`);
      toast.success("Deleted");
      load();
    }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  // Split by tab, then apply search + date filter + sort.
  const paidCount = items.filter((r) => r.payment_status === "paid").length;
  const unpaidCount = items.filter((r) => r.payment_status !== "paid").length;

  const filtered = useMemo(() => {
    const dateField = DATE_KEYS.find((k) => k.id === dateKey)?.field || "created_at";
    let list = items.filter((r) => (tab === "paid" ? r.payment_status === "paid" : r.payment_status !== "paid"));
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(q)
      || (r.customer_email || "").toLowerCase().includes(q)
      || (r.short_code || "").toLowerCase().includes(q)
      || (r.id || "").toLowerCase().includes(q)
      || (r.status || "").toLowerCase().includes(q)
    );
    if (fromDate) list = list.filter((r) => (r[dateField] || "").slice(0, 10) >= fromDate);
    if (toDate) list = list.filter((r) => (r[dateField] || "").slice(0, 10) <= toDate);

    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const va = a[key] ?? "";
      const vb = b[key] ?? "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va).localeCompare(String(vb)) * mul;
    };
    return [...list].sort(cmp);
  }, [items, tab, query, dateKey, fromDate, toDate, sort]);

  const paged = filtered.slice(0, pageSize);

  const clearFilters = () => {
    setQuery(""); setFromDate(""); setToDate(""); setDateKey("purchase");
  };

  const exportCsv = () => {
    const header = ["ID","Customer","Email","Purchased","Delivery","Pickup","Address","Total","Tax","Payout","Payment Status","Status"];
    const rows = filtered.map((r) => [
      r.short_code || r.id,
      r.customer_name || "",
      r.customer_email || "",
      shortDate(r.created_at),
      r.delivery_date,
      r.pickup_date,
      r.delivery_resort || "",
      Number(r.total || 0).toFixed(2),
      Number(r.tax || 0).toFixed(2),
      (Number(r.total || 0) - Number(r.tax || 0)).toFixed(2),
      r.payment_status || "",
      r.status || "",
    ]);
    const csv = [header, ...rows].map((row) =>
      row.map((cell) => {
        const s = String(cell ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `reservations_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(`Exported ${rows.length} reservation${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-4xl">All Reservations</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn onClick={() => setNewBookingOpen(true)} data-testid="res-new-booking-btn"><Plus className="w-3.5 h-3.5" /> New phone booking</Btn>
          <Link
            to={`/admin/labels?day=${new Date().toISOString().slice(0,10)}&autoprint=1`}
            target="_blank" rel="noreferrer"
            className="text-xs px-3 py-2 rounded-lg border border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/10 inline-flex items-center gap-1.5"
            data-testid="res-print-today-tags"
            title="Opens the Customer Tags page for today and fires the print dialog automatically"
          >
            <Printer className="w-3.5 h-3.5" /> Print today's tags
          </Link>
          <button
            type="button"
            onClick={() => setPurgeOpen(true)}
            className="text-xs px-3 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1.5"
            data-testid="res-reset-btn"
            title="Bulk-delete reservations (for resetting test data before launch)"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Reset test data
          </button>
          <button
            type="button"
            onClick={() => setCleanupOpen(true)}
            className="text-xs px-3 py-2 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 inline-flex items-center gap-1.5"
            data-testid="res-cleanup-expired-btn"
            title="Preview and clean up expired unpaid reservations"
          >
            <CalendarX className="w-3.5 h-3.5" /> Cleanup expired unpaid
          </button>
        </div>
      </div>

      {/* Search + date range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-[#B9C8D5] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ID, Customer Name, Status"
            className="pl-9"
            data-testid="res-search"
          />
        </div>
        <div className="flex items-center gap-2 bg-black/30 border border-[#FFD166]/25 rounded-lg px-3 py-2 relative">
          <Calendar className="w-4 h-4 text-[#B9C8D5] shrink-0" />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="bg-transparent text-[#F6F1E4] text-sm outline-none flex-1 min-w-0"
            data-testid="res-date-from"
            placeholder="From"
          />
          <span className="text-[#B9C8D5] text-xs">→</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="bg-transparent text-[#F6F1E4] text-sm outline-none flex-1 min-w-0"
            data-testid="res-date-to"
            placeholder="To"
          />
        </div>
      </div>

      {/* Second toolbar row */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
        <div className="flex items-center gap-3 text-sm text-[#B9C8D5]">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-2 py-1 rounded-lg"
            data-testid="res-page-size"
          >
            {[10, 25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>Entries</span>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {DATE_KEYS.map((k) => (
            <label key={k.id} className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${dateKey === k.id ? "text-[#FFD166]" : "text-[#B9C8D5] hover:text-[#F6F1E4]"}`} data-testid={`res-datekey-${k.id}`}>
              <input
                type="radio"
                name="res-datekey"
                checked={dateKey === k.id}
                onChange={() => setDateKey(k.id)}
                className="accent-[#FFD166]"
              />
              {k.label}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-[#B9C8D5] hover:text-[#FFD166] underline underline-offset-2"
            data-testid="res-clear-filters"
          >
            Clear all filters
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className="text-xs text-[#FFD166] hover:text-[#F4A93A] inline-flex items-center gap-1 underline underline-offset-2"
            data-testid="res-export-csv"
          >
            <Download className="w-3 h-3" /> Export to CSV
          </button>
        </div>
      </div>

      {/* Tabs — Paid / Unpaid with count badges */}
      <div className="flex items-center gap-6 border-b border-[#FFD166]/20 mb-4" data-testid="res-tabs">
        <button
          type="button"
          onClick={() => setTab("paid")}
          className={`pb-3 -mb-px border-b-2 text-sm font-medium transition-colors ${tab === "paid" ? "border-[#FFD166] text-[#FFD166]" : "border-transparent text-[#B9C8D5] hover:text-[#F6F1E4]"}`}
          data-testid="res-tab-paid"
        >
          Paid Reservations
        </button>
        <button
          type="button"
          onClick={() => setTab("unpaid")}
          className={`pb-3 -mb-px border-b-2 text-sm font-medium transition-colors inline-flex items-center gap-2 ${tab === "unpaid" ? "border-[#FFD166] text-[#FFD166]" : "border-transparent text-[#B9C8D5] hover:text-[#F6F1E4]"}`}
          data-testid="res-tab-unpaid"
        >
          Unpaid Reservations
          {unpaidCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-500/90 text-[#F6F1E4] text-[10px] font-semibold border border-rose-400/60">
              {unpaidCount > 99 ? "99+" : unpaidCount}
            </span>
          )}
        </button>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-[#B9C8D5] pb-3">
          {filtered.length} {tab === "paid" ? `of ${paidCount}` : `of ${unpaidCount}`}
        </span>
      </div>

      {/* Reservation table */}
      <Card className="overflow-hidden" data-testid="res-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 border-b border-[#FFD166]/15">
              <tr>
                <th className="text-left px-4 py-3"><SortHeader label="ID" sortKey="short_code" sort={sort} setSort={setSort} /></th>
                <th className="text-left px-4 py-3"><SortHeader label="Customer" sortKey="customer_name" sort={sort} setSort={setSort} /></th>
                <th className="text-left px-4 py-3"><SortHeader label="Purchased" sortKey="created_at" sort={sort} setSort={setSort} /></th>
                <th className="text-left px-4 py-3"><SortHeader label="Rental Dates" sortKey="delivery_date" sort={sort} setSort={setSort} /></th>
                <th className="text-left px-4 py-3 hidden lg:table-cell"><SortHeader label="Address" sortKey="delivery_resort" sort={sort} setSort={setSort} /></th>
                <th className="text-right px-4 py-3"><SortHeader label="Total" sortKey="total" sort={sort} setSort={setSort} align="right" /></th>
                <th className="text-right px-4 py-3 hidden md:table-cell"><SortHeader label="Taxes" sortKey="tax" sort={sort} setSort={setSort} align="right" /></th>
                <th className="text-right px-4 py-3"><span className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">Payout</span></th>
                <th className="text-right px-4 py-3"><span className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">Status</span></th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-[#B9C8D5] py-12" data-testid="res-empty">
                    No reservations match these filters.
                  </td>
                </tr>
              ) : paged.map((r) => {
                const total = Number(r.total || 0);
                const tax = Number(r.tax || 0);
                const payout = total - tax;
                const late = isLateStart(r);
                return (
                  <tr
                    key={r.id}
                    onClick={() => setDetailTarget(r)}
                    className={`border-b cursor-pointer transition-colors ${late ? "border-red-500/30 bg-red-500/10 hover:bg-red-500/20" : "border-[#FFD166]/8 hover:bg-black/30"}`}
                    data-testid={`res-row-${r.id}`}
                    data-late={late ? "true" : undefined}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-[#FFD166] font-mono text-xs">{r.short_code || `#${(r.id || "").slice(0, 6).toUpperCase()}`}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#F6F1E4]">{r.customer_name || "—"}</div>
                      <div className="text-[11px] text-[#B9C8D5] truncate max-w-[220px]">{r.customer_email || ""}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#B9C8D5] tabular-nums">{shortDate(r.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#F6F1E4] tabular-nums">
                      {r.delivery_date}<span className="text-[#B9C8D5]"> → </span>{r.pickup_date}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell max-w-[240px]">
                      <div className="text-[#F6F1E4] truncate">{r.delivery_resort || "—"}</div>
                      <div className="text-[10px] text-[#B9C8D5]">Hotel</div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap text-[#F6F1E4] tabular-nums">{money(total)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums hidden md:table-cell">
                      <div className="text-[#F6F1E4]">{money(tax)}</div>
                      <div className="text-[9px] text-[#B9C8D5] leading-tight italic">Collected by Enchanted</div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums text-[#FFD166] font-medium">{money(payout)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {r.is_test_order && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-violet-500/20 text-violet-300 border border-violet-500/40" data-testid={`res-test-badge-${r.id}`}>Test</span>
                        )}
                        <StatusChip payment_status={r.payment_status} status={r.status} late={late} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > pageSize && (
          <div className="px-4 py-3 text-xs text-[#B9C8D5] border-t border-[#FFD166]/10 text-center" data-testid="res-more-hint">
            Showing first {pageSize} of {filtered.length} — bump the Entries selector above to see more.
          </div>
        )}
      </Card>

      {messageTarget && <MessageModal reservation={messageTarget} onClose={() => setMessageTarget(null)} />}
      {detailTarget && <ReservationDetail reservation={detailTarget} productsById={productsById} onClose={() => setDetailTarget(null)} onCancel={(r) => { setDetailTarget(null); setCancelTarget(r); }} onExtraCharge={(r) => { setDetailTarget(null); setExtraChargeTarget(r); }} onEdit={(r) => { setDetailTarget(null); setEditTarget(r); }} onReload={load} />}
      {cancelTarget && <CancelRefundModal reservation={cancelTarget} onClose={() => setCancelTarget(null)} onDone={() => { setCancelTarget(null); load(); }} />}
      {extraChargeTarget && <ExtraChargeModal reservation={extraChargeTarget} onClose={() => setExtraChargeTarget(null)} onDone={() => { setExtraChargeTarget(null); load(); }} />}
      {editTarget && <EditReservationModal reservation={editTarget} products={Object.values(productsById)} onClose={() => setEditTarget(null)} onDone={(delta, meta) => { setEditTarget(null); load(); if (delta > 0) setExtraChargeTarget({ ...editTarget, _prefill_amount: delta, _prefill_desc: "Booking edit — additional charge", _notify_reschedule: meta?.notifyCustomer && meta?.datesChanged, _new_delivery_date: meta?.deliveryDate, _new_pickup_date: meta?.pickupDate }); }} />}
      {purgeOpen && <PurgeModal onClose={() => setPurgeOpen(false)} onDone={() => { setPurgeOpen(false); load(); }} />}
      {newBookingOpen && <NewBookingModal onClose={() => setNewBookingOpen(false)} onDone={() => { setNewBookingOpen(false); load(); }} />}
      {cleanupOpen && <ExpiredUnpaidCleanupModal onClose={() => setCleanupOpen(false)} onDone={() => { setCleanupOpen(false); load(); }} />}
    </div>
  );
};

const PurgeModal = ({ onClose, onDone }) => {
  const [scope, setScope] = useState("cancelled");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const scopeLabels = {
    cancelled: "Only cancelled reservations (safest)",
    test: "All test data (cancelled + unpaid > 24h old)",
    all: "EVERYTHING — every reservation ever booked",
  };
  const run = async () => {
    if (confirmText !== "DELETE") { toast.error("Type DELETE exactly to confirm."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/purge`, { scope, confirm: "DELETE" });
      toast.success(`Deleted ${data.deleted} reservation${data.deleted === 1 ? "" : "s"} · Released ${data.released_units} inventory unit${data.released_units === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="purge-modal">
      <div className="glass max-w-md w-full p-8 relative border-2 border-red-500/40" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="purge-close"><XIcon className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-6 h-6 text-red-400" />
          <h2 className="font-serif text-2xl text-red-300">Reset test data</h2>
        </div>
        <p className="text-sm text-[#F6F1E4]/85 leading-relaxed">
          This <b>hard-deletes</b> reservations forever. Use it to clear pre-launch test data before you start taking real bookings. Assigned inventory units will be released.
        </p>

        <div className="mt-5 space-y-2" data-testid="purge-scope">
          {Object.entries(scopeLabels).map(([val, label]) => (
            <label key={val} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${scope === val ? "border-[#FFD166] bg-[#FFD166]/10" : "border-[#B9C8D5]/20 hover:border-[#FFD166]/40"}`} data-testid={`purge-scope-${val}`}>
              <input type="radio" name="purge-scope" value={val} checked={scope === val} onChange={() => setScope(val)} className="mt-1 accent-[#FFD166]" />
              <span className="text-sm text-[#F6F1E4]">{label}</span>
            </label>
          ))}
        </div>

        <label className="block mt-5 text-sm">
          <span className="eyebrow text-red-300">Type <b>DELETE</b> to confirm</span>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="mt-1 font-mono" data-testid="purge-confirm-input" />
        </label>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <Btn variant="danger" onClick={run} disabled={busy || confirmText !== "DELETE"} data-testid="purge-run-btn">
            <Trash2 className="w-4 h-4" /> {busy ? "Deleting…" : "Delete forever"}
          </Btn>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
};

/* ------------ Photo strip (delivery + return) ------------ */
const PhotoStrip = ({ reservation: r, onChange }) => {
  const [photos, setPhotos] = React.useState(r.photos || []);
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const inputDeliveryCameraRef = React.useRef(null);
  const inputDeliveryGalleryRef = React.useRef(null);
  const inputReturnCameraRef = React.useRef(null);
  const inputReturnGalleryRef = React.useRef(null);

  const upload = async (kind, files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const compressed = await compressFiles(files);
      const fd = new FormData();
      compressed.slice(0, 4).forEach((f) => fd.append("files", f));
      const { data } = await axios.post(
        `${API}/admin/reservations/${r.id}/photos?kind=${kind}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPhotos((p) => [...p, ...data.photos]);
      toast.success(`${data.photos.length} photo${data.photos.length > 1 ? "s" : ""} uploaded`);
      onChange?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Delete this photo? Cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/reservations/${r.id}/photos/${photoId}`);
      setPhotos((p) => p.filter((x) => x.id !== photoId));
      toast.success("Photo removed");
      onChange?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const deliveryPhotos = photos.filter((p) => p.kind === "delivery");
  const returnPhotos = photos.filter((p) => p.kind === "return");

  const Section = ({ label, testid, kind, list, cameraRef, galleryRef }) => (
    <div className="mb-4" data-testid={`photos-${kind}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="eyebrow">{label} ({list.length}/4)</div>
        {list.length < 4 && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
              className="text-[10px] uppercase tracking-widest text-[#FFD166] hover:text-[#F4A93A] flex items-center gap-1"
              data-testid={`photo-camera-${kind}`}
            >
              <Camera className="w-3 h-3" /> Camera
            </button>
            <span className="text-[#FFD166]/30 text-xs">|</span>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              disabled={busy}
              className="text-[10px] uppercase tracking-widest text-[#FFD166] hover:text-[#F4A93A] flex items-center gap-1"
              data-testid={`photo-add-${kind}`}
            >
              <ImagePlus className="w-3 h-3" /> Upload
            </button>
          </div>
        )}
        {/* Camera input */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => { upload(kind, e.target.files); e.target.value = ""; }}
          data-testid={`photo-camera-input-${kind}`}
        />
        {/* Gallery/file input */}
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { upload(kind, e.target.files); e.target.value = ""; }}
          data-testid={`photo-input-${kind}`}
        />
      </div>
      {list.length === 0 ? (
        <p className="text-[11px] text-[#B9C8D5]/70 italic">No {label.toLowerCase()} yet.</p>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {list.map((p) => (
            <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden border border-[#FFD166]/25 bg-black/40" data-testid={`photo-thumb-${p.id}`}>
              <img
                src={`${BACKEND_URL}${p.url}`}
                alt={label}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setPreview(p)}
              />
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-500/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                data-testid={`photo-delete-${p.id}`}
                aria-label="Delete photo"
              >
                <Trash className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="mt-6 pt-4 border-t border-[#FFD166]/15" data-testid="res-detail-photos">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="w-4 h-4 text-[#FFD166]" />
          <h3 className="font-serif text-lg text-[#FFD166]">Photos</h3>
        </div>
        <Section label="Drop-off photos" kind="delivery" list={deliveryPhotos} cameraRef={inputDeliveryCameraRef} galleryRef={inputDeliveryGalleryRef} />
        <Section label="Return / damage photos" kind="return" list={returnPhotos} cameraRef={inputReturnCameraRef} galleryRef={inputReturnGalleryRef} />
      </div>
      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="photo-lightbox">
          <img src={`${BACKEND_URL}${preview.url}`} alt="" className="max-w-full max-h-[90vh] rounded-lg" />
          <button type="button" onClick={() => setPreview(null)} className="absolute top-4 right-4 text-white" data-testid="photo-lightbox-close">
            <XIcon className="w-6 h-6" />
          </button>
        </div>
      )}
    </>
  );
};


const ACTIVITY_TYPE_STYLES = {
  payment:          { icon: ArrowDownCircle, color: "text-emerald-300", ring: "border-emerald-500/40 bg-emerald-500/5",   sign: "+" },
  charge:           { icon: ArrowDownCircle, color: "text-emerald-300", ring: "border-emerald-500/40 bg-emerald-500/5",   sign: "+" },
  refund:           { icon: ArrowUpCircle,   color: "text-amber-300",   ring: "border-amber-500/40 bg-amber-500/5",       sign: "−" },
  cancel:           { icon: Ban,             color: "text-rose-300",    ring: "border-rose-500/40 bg-rose-500/5",         sign: "−" },
  edit:             { icon: PenSquare,       color: "text-[#FFD166]",   ring: "border-[#FFD166]/30 bg-[#FFD166]/5",       sign: "" },
  change:           { icon: RefreshCw,       color: "text-[#FFD166]",   ring: "border-[#FFD166]/30 bg-[#FFD166]/5",       sign: "" },
  delivery:         { icon: Truck,           color: "text-sky-300",     ring: "border-sky-500/40 bg-sky-500/5",           sign: "" },
  info:             { icon: Info,            color: "text-[#B9C8D5]",   ring: "border-white/10 bg-white/5",               sign: "" },
  payout_pending:   { icon: Banknote,        color: "text-indigo-300",  ring: "border-indigo-500/40 bg-indigo-500/5",     sign: "→" },
  payout_processed: { icon: Banknote,        color: "text-emerald-300", ring: "border-emerald-500/40 bg-emerald-500/5",   sign: "✓" },
  payout_failed:    { icon: AlertTriangle,   color: "text-rose-300",    ring: "border-rose-500/40 bg-rose-500/5",         sign: "!" },
  payout_cancelled: { icon: Ban,             color: "text-[#B9C8D5]",   ring: "border-white/10 bg-white/5",               sign: "" },
};

const _fmtWhen = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  // Payout scheduled_for entries are pure YYYY-MM-DD dates; everything else is ISO datetime.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (_) { return s; }
};


/* ── Needs Review section ── */
const NeedsReviewSection = ({ reservationId, onResolved }) => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flagging, setFlagging] = useState(false);
  const [resolveId, setResolveId] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reservations/${reservationId}/financial-breakdown`);
      setFlags(data.needs_review_flags || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [reservationId]);

  useEffect(() => { load(); }, [load]);

  const activeFlags = flags.filter((f) => !f.resolved);

  const submitFlag = async () => {
    if (!flagReason.trim()) { toast.error("Reason is required"); return; }
    setFlagging(true);
    try {
      await axios.post(`${API}/admin/reservations/${reservationId}/flag-needs-review`, { reason: flagReason });
      toast.warning("Reservation flagged — funds held until resolved");
      setFlagOpen(false); setFlagReason("");
      load(); onResolved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Flag failed"); }
    finally { setFlagging(false); }
  };

  const submitResolve = async () => {
    if (!resolveNote.trim()) { toast.error("Resolution note is required"); return; }
    setResolving(true);
    try {
      await axios.post(`${API}/admin/reservations/${reservationId}/needs-review/${resolveId}/resolve`, { resolution_note: resolveNote });
      toast.success("Flag resolved");
      setResolveId(null); setResolveNote("");
      load(); onResolved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
    finally { setResolving(false); }
  };

  if (loading) return null;

  return (
    <div className="mt-6" data-testid="needs-review-section">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Needs Review
          {activeFlags.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] bg-red-500/20 text-red-300 border border-red-500/30">
              {activeFlags.length} active
            </span>
          )}
        </span>
        {activeFlags.length === 0 && (
          <button
            onClick={() => setFlagOpen((o) => !o)}
            className="text-[11px] text-[#B9C8D5] hover:text-[#FFD166] flex items-center gap-1"
            data-testid="nr-flag-toggle"
          >
            <Flag className="w-3 h-3" /> Flag for review
          </button>
        )}
      </div>

      {flagOpen && activeFlags.length === 0 && (
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 space-y-2 mb-3">
          <p className="text-[11px] text-[#B9C8D5]">Flagging will hold all pending payout entries for this reservation until you resolve the flag.</p>
          <textarea
            rows={2}
            className="w-full rounded-lg bg-black/40 border border-[#FFD166]/20 text-sm text-[#F6F1E4] p-2 placeholder-[#B9C8D5]/50 resize-none"
            placeholder="Describe the issue (e.g. 'Customer disputes charge — awaiting bank confirmation')"
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            data-testid="nr-flag-reason"
          />
          <div className="flex gap-2">
            <button onClick={() => setFlagOpen(false)} className="text-xs px-3 py-1.5 rounded-lg border border-[#B9C8D5]/25 text-[#B9C8D5]">Cancel</button>
            <button onClick={submitFlag} disabled={flagging} className="text-xs px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50" data-testid="nr-flag-submit">
              {flagging ? "Flagging…" : "Confirm flag"}
            </button>
          </div>
        </div>
      )}

      {flags.length === 0 && !flagOpen && (
        <p className="text-[11px] text-[#B9C8D5]/60 italic">No review flags.</p>
      )}

      {flags.map((f) => (
        <div key={f.id} className={`rounded-lg border p-3 mb-2 ${f.resolved ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/30 bg-red-500/8"}`}
             data-testid={`nr-flag-${f.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border mr-1 ${f.resolved ? "text-emerald-300 border-emerald-500/30" : f.flag_type === "manual" ? "text-amber-300 border-amber-500/40" : "text-red-300 border-red-500/40"}`}>
                {f.resolved ? "Resolved" : f.flag_type === "manual" ? "Manual" : "System"}
              </span>
              <span className="text-[11px] font-mono text-[#B9C8D5]">{f.condition_key}</span>
            </div>
            {!f.resolved && <span className="text-red-300 font-mono text-sm">${Number(f.amount_held).toFixed(2)} held</span>}
          </div>
          <p className="text-sm text-[#F6F1E4] mt-1">{f.reason}</p>
          <p className="text-[10px] text-[#B9C8D5] mt-0.5">
            {new Date(f.flagged_at).toLocaleString()} · by {f.flagged_by}
          </p>
          {f.resolved && (
            <p className="text-[11px] text-emerald-300 mt-1">
              Resolved {new Date(f.resolved_at).toLocaleString()} by {f.resolved_by}: {f.resolution_note}
            </p>
          )}
          {!f.resolved && (
            resolveId === f.id ? (
              <div className="mt-2 space-y-2">
                <textarea rows={2} className="w-full rounded-lg bg-black/40 border border-[#FFD166]/20 text-sm text-[#F6F1E4] p-2 placeholder-[#B9C8D5]/50 resize-none"
                  placeholder="Describe what was verified and why it is safe to release these funds…"
                  value={resolveNote} onChange={(e) => setResolveNote(e.target.value)}
                  data-testid={`nr-resolve-note-${f.id}`}
                />
                <div className="flex gap-2">
                  <button onClick={() => { setResolveId(null); setResolveNote(""); }} className="text-xs px-3 py-1.5 rounded-lg border border-[#B9C8D5]/25 text-[#B9C8D5]">Cancel</button>
                  <button onClick={submitResolve} disabled={resolving} className="text-xs px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 disabled:opacity-50" data-testid={`nr-confirm-resolve-${f.id}`}>
                    {resolving ? "Resolving…" : "Confirm resolution"}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => { setResolveId(f.id); setResolveNote(""); }} className="mt-2 text-xs px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`nr-resolve-btn-${f.id}`}>
                Resolve with note
              </button>
            )
          )}
        </div>
      ))}
    </div>
  );
};


/* ── Financial Breakdown section (collapsible) ── */
const KIND_LABEL = {
  care: "Care fee",
  pom_fee: "PoM fee",
  pom_full: "PoM · full balance",
  standard_50_1: "Standard 50% (day −15)",
  standard_50_2: "Standard 50% (day −7)",
};

/* Recalculate confirmation modal */
const RecalculateModal = ({ preview, reservationId, onClose, onDone }) => {
  const [confirming, setConfirming] = useState(false);
  if (!preview) return null;
  const p = preview.preview || preview;

  const confirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/admin/reservations/${reservationId}/payout/recalculate`);
      toast.success("Payout schedule recalculated");
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Recalculation failed"); }
    finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/75 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A1628] border border-[#FFD166]/20 rounded-2xl p-5 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-[#FFD166] text-lg">Recalculate Payouts</h3>
          <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#FFD166]"><XIcon className="w-4 h-4" /></button>
        </div>

        {preview.status === "reserve_shortfall" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 text-xs text-red-300">{preview.message}</div>
        )}
        {preview.status === "fully_paid" && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-4 text-xs text-emerald-300">{preview.message}</div>
        )}

        {/* Before/after breakdown */}
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/30 rounded-xl p-3 border border-[#FFD166]/10">
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-2">Revenue base</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Net collected</span><span className="font-mono text-[#F6F1E4]">{moneyD(preview.net_collected)}</span></div>
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Sales tax</span><span className="font-mono text-orange-300">−{moneyD(preview.sales_tax)}</span></div>
                {preview.affiliate_commission > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Affiliate</span><span className="font-mono text-purple-300">−{moneyD(preview.affiliate_commission)}</span></div>}
                <div className="flex justify-between border-t border-[#FFD166]/10 pt-1"><span className="text-[#F6F1E4] font-medium">Revenue</span><span className="font-mono text-[#FFD166] font-medium">{moneyD(preview.revenue)}</span></div>
              </div>
            </div>
            <div className="bg-black/30 rounded-xl p-3 border border-[#FFD166]/10">
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-2">Federal reserve</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Required</span><span className="font-mono text-orange-300">{moneyD(preview.required_reserve)}</span></div>
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Already deducted</span><span className="font-mono text-emerald-300">{moneyD(preview.already_reserved)}</span></div>
                <div className="flex justify-between border-t border-[#FFD166]/10 pt-1"><span className="text-[#F6F1E4] font-medium">Outstanding</span><span className={`font-mono font-medium ${preview.outstanding_reserve > 0 ? "text-amber-300" : "text-emerald-300"}`}>{moneyD(preview.outstanding_reserve)}</span></div>
              </div>
            </div>
          </div>

          {/* Old pending entries */}
          {preview.old_pending_entries?.length > 0 && (
            <div>
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-1.5">Entries to be replaced</p>
              {preview.old_pending_entries.map((e) => (
                <div key={e.id} className="flex justify-between items-center py-1 border-b border-[#FFD166]/5 text-red-300">
                  <span>{KIND_LABEL[e.kind] || e.kind} · {e.scheduled_for}</span>
                  <span className="font-mono line-through opacity-60">{moneyD(e.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {/* New entries */}
          {preview.new_entries?.length > 0 && (
            <div>
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-1.5">New entries</p>
              {preview.new_entries.map((e, i) => (
                <div key={i} className="py-1.5 border-b border-[#FFD166]/5">
                  <div className="flex justify-between items-center">
                    <span className="text-[#F6F1E4]">{KIND_LABEL[e.kind] || e.kind}</span>
                    <div className="text-right">
                      <span className="font-mono text-emerald-300 font-medium">{moneyD(e.amount)}</span>
                      {e.federal_reserve_deducted > 0 && <span className="block text-[9px] text-orange-300/80">−{moneyD(e.federal_reserve_deducted)} reserve</span>}
                    </div>
                  </div>
                  <span className="text-[#B9C8D5]/60">Release: {e.scheduled_for}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 font-medium">
                <span className="text-[#F6F1E4]">Total to bank</span>
                <span className="font-mono text-emerald-300">{moneyD(preview.new_total_to_bank || preview.new_entries.reduce((s,e)=>s+e.amount,0))}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-[#B9C8D5]/20 text-[#B9C8D5] text-sm">Cancel</button>
          {preview.status === "ok" && (
            <button
              onClick={confirm} disabled={confirming}
              className="flex-1 py-2.5 rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm hover:bg-emerald-500/20 disabled:opacity-50"
              data-testid="recalculate-confirm-btn"
            >
              {confirming ? "Recalculating…" : "Confirm recalculation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* Early release confirmation */
const EarlyReleaseModal = ({ entry, reservationId, onClose, onDone }) => {
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!entry) return;
    axios.post(`${API}/admin/payouts/${entry.id}/early-release`, {})
      .then(r => setPreview(r.data.preview))
      .catch(() => {});
  }, [entry]);

  if (!entry) return null;

  const confirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/admin/payouts/${entry.id}/early-release`, { confirm: true });
      toast.success("Entry moved to today's sweep");
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Early release failed"); }
    finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/75 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A1628] border border-[#FFD166]/20 rounded-2xl p-5 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-[#FFD166]">Release Early</h3>
          <button onClick={onClose}><XIcon className="w-4 h-4 text-[#B9C8D5]" /></button>
        </div>
        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between"><span className="text-[#B9C8D5]">Entry</span><span className="text-[#F6F1E4]">{KIND_LABEL[entry.kind] || entry.kind}</span></div>
          <div className="flex justify-between"><span className="text-[#B9C8D5]">Amount (net)</span><span className="font-mono text-emerald-300 font-medium">{moneyD(entry.amount)}</span></div>
          {entry.gross_amount > entry.amount && <div className="flex justify-between"><span className="text-[#B9C8D5]">Reserve deducted</span><span className="font-mono text-orange-300">{moneyD(entry.federal_reserve_deducted)}</span></div>}
          <div className="flex justify-between"><span className="text-[#B9C8D5]">Currently scheduled</span><span className="text-amber-300">{entry.scheduled_for}</span></div>
          <div className="flex justify-between"><span className="text-[#B9C8D5]">New release date</span><span className="text-emerald-300">Today (next sweep)</span></div>
        </div>
        <p className="text-xs text-[#B9C8D5]/70 mb-4">This moves the entry to today's nightly sweep. The cancellation protection window will no longer apply to this entry.</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-[#B9C8D5]/20 text-[#B9C8D5] text-sm">Cancel</button>
          <button onClick={confirm} disabled={confirming}
            className="flex-1 py-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm hover:bg-amber-500/20 disabled:opacity-50"
            data-testid="early-release-confirm-btn"
          >
            {confirming ? "Releasing…" : "Release now"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FinancialBreakdownSection = ({ reservationId, reloadKey }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recalcPreview, setRecalcPreview] = useState(null);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [earlyEntry, setEarlyEntry] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/reservations/${reservationId}/financial-breakdown`);
      setData(d);
    } catch { toast.error("Couldn't load financial breakdown"); }
    finally { setLoading(false); }
  }, [reservationId]);

  useEffect(() => { if (open) load(); }, [open, reloadKey, load]);

  const openRecalculate = async () => {
    setRecalcLoading(true);
    try {
      const { data: p } = await axios.get(`${API}/admin/reservations/${reservationId}/payout/recalculate`);
      setRecalcPreview(p);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setRecalcLoading(false); }
  };

  const retryEntry = async (entryId) => {
    setRetrying(entryId);
    try {
      await axios.post(`${API}/admin/payouts/${entryId}/retry`);
      toast.success("Entry marked for retry");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    finally { setRetrying(null); }
  };

  const Row = ({ label, value, color, bold, indent, testid, sub }) => (
    <div className={`flex justify-between py-0.5 ${indent ? "pl-4" : ""} ${bold ? "border-t border-[#FFD166]/10 mt-1 pt-2" : ""}`} data-testid={testid}>
      <span className={`text-xs ${bold ? "text-[#F6F1E4] font-medium" : sub ? "text-[#B9C8D5]/60 italic" : "text-[#B9C8D5]"}`}>{label}</span>
      <span className={`text-xs tabular-nums font-mono ${color || (bold ? "text-[#F6F1E4]" : "text-[#B9C8D5]")}`}>{value}</span>
    </div>
  );

  return (
    <>
      {recalcPreview && (
        <RecalculateModal
          preview={recalcPreview}
          reservationId={reservationId}
          onClose={() => setRecalcPreview(null)}
          onDone={() => { load(); }}
        />
      )}
      {earlyEntry && (
        <EarlyReleaseModal
          entry={earlyEntry}
          reservationId={reservationId}
          onClose={() => setEarlyEntry(null)}
          onDone={() => { load(); }}
        />
      )}

      <div className="mt-6 border border-[#FFD166]/15 rounded-xl" data-testid="financial-breakdown-section">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between p-4 text-left"
          data-testid="financial-breakdown-toggle"
        >
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#FFD166]" />
            <span className="eyebrow">Financial Breakdown</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#B9C8D5] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="px-4 pb-4" data-testid="financial-breakdown-content">
            {loading ? (
              <p className="text-[#B9C8D5] text-sm">Loading…</p>
            ) : data ? (
              <div className="space-y-0.5">
                <Row label="Customer total charged" value={moneyD(data.customer_total_charged)} bold color="text-[#FFD166]" testid="bd-total" />
                <Row label="Rental subtotal" value={moneyD(data.rental_subtotal)} indent />
                <Row label="Delivery fee" value={moneyD(data.delivery_fee)} indent />
                <Row label="Sales tax collected" value={moneyD(data.sales_tax_collected)} indent color="text-orange-300" testid="bd-sales-tax" />
                {data.driver_tip > 0 && <Row label="Driver tip" value={moneyD(data.driver_tip)} indent />}
                {data.discounts > 0 && <Row label="Discounts applied" value={`-${moneyD(data.discounts)}`} indent color="text-emerald-300" />}
                {data.refund_amount > 0 && <Row label="Refunds issued" value={`-${moneyD(data.refund_amount)}`} indent color="text-red-300" />}
                {data.affiliate_commission > 0 && (
                  <>
                    <Row label="Affiliate commission (reserved)" value={moneyD(data.affiliate_commission)} indent color="text-purple-300" />
                    {data.unpaid_commission > 0 && <Row label="Unpaid commission" value={moneyD(data.unpaid_commission)} indent sub color="text-purple-300/70" />}
                  </>
                )}
                <Row label="Stripe fee (est.)" value={moneyD(data.stripe_fee_est)} indent color="text-[#B9C8D5]/60" />

                {data.has_pom && (
                  <div className="mt-2 pt-2 border-t border-[#FFD166]/10 space-y-0.5">
                    <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-1">Cancellation Protection Split</p>
                    <Row label="PoM fee (non-refundable)" value={moneyD(data.pom_fee)} color="text-[#FFD166]" />
                    <Row label="PoM balance status" value={data.pom_full_payout ? `${data.pom_full_payout.status} · ${data.pom_full_payout.scheduled_for || "—"}` : "—"} indent />
                  </div>
                )}

                {/* Payout Schedule */}
                <div className="mt-2 pt-2 border-t border-[#FFD166]/10 space-y-0.5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">Payout Schedule</p>
                    <button
                      onClick={openRecalculate}
                      disabled={recalcLoading}
                      className="text-[10px] px-2 py-1 rounded-lg border border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10 disabled:opacity-50 flex items-center gap-1"
                      data-testid="recalculate-btn"
                    >
                      <RefreshCw className={`w-2.5 h-2.5 ${recalcLoading ? "animate-spin" : ""}`} />
                      Recalculate
                    </button>
                  </div>

                  <Row label="Already paid to bank" value={moneyD(data.already_paid_out)} color="text-emerald-300" testid="bd-paid-out" />
                  <Row label="Protected (inside window)" value={moneyD(data.cancellation_protected)} color="text-amber-300" />
                  <Row label="Eligible (window cleared)" value={moneyD(data.eligible_payout_gross)} color="text-sky-300" testid="bd-eligible" />

                  {/* Active entries with per-entry actions */}
                  {data.payout_entries.map((e) => (
                    <div key={e.id} className="pl-4 py-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-[#B9C8D5]">{KIND_LABEL[e.kind] || e.kind}</span>
                          <span className={`ml-1 text-xs font-mono ${e.status === "processed" ? "text-emerald-300" : e.status === "failed" ? "text-red-300" : "text-[#B9C8D5]"}`}>
                            {moneyD(e.amount)}
                          </span>
                          {e.federal_reserve_deducted > 0 && (
                            <span className="ml-1 text-[10px] text-orange-300/70">(−{moneyD(e.federal_reserve_deducted)} reserve)</span>
                          )}
                          <span className="ml-1 text-[10px] text-[#B9C8D5]/50">{e.status}{e.scheduled_for ? ` · ${e.scheduled_for}` : ""}</span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {(e.status === "failed" || e.status === "cancelled") && (
                            <button
                              onClick={() => retryEntry(e.id)}
                              disabled={retrying === e.id}
                              className="text-[9px] px-1.5 py-0.5 rounded border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50"
                              data-testid={`retry-btn-${e.id}`}
                            >
                              {retrying === e.id ? "…" : "Retry"}
                            </button>
                          )}
                          {e.status === "pending" && (e.scheduled_for > new Date().toISOString().slice(0,10)) && (
                            <button
                              onClick={() => setEarlyEntry(e)}
                              className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                              data-testid={`early-release-btn-${e.id}`}
                            >
                              Release early
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Cancelled entries (collapsed by default) */}
                  {data.cancelled_entries?.length > 0 && (
                    <div className="pl-4 mt-1">
                      <p className="text-[10px] text-[#B9C8D5]/50 italic">
                        {data.cancelled_entries.length} cancelled entr{data.cancelled_entries.length === 1 ? "y" : "ies"} · {moneyD(data.cancelled_payout_total)} total
                      </p>
                    </div>
                  )}

                  {/* Unscheduled available — shown when all entries cancelled */}
                  {data.unscheduled_available > 0.01 && data.payout_entries.length === 0 && (
                    <div className="mt-2 p-2 rounded-lg border border-amber-500/20 bg-amber-500/5">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-amber-300 font-medium">{moneyD(data.unscheduled_available)} available in Stripe — no active schedule</p>
                          <p className="text-[10px] text-[#B9C8D5] mt-0.5">All payout entries are cancelled. Click "Recalculate" to rebuild the schedule.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Reserves */}
                <div className="mt-2 pt-2 border-t border-[#FFD166]/10 space-y-0.5">
                  <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-1">Federal Tax Savings Reserve</p>
                  <Row label="Revenue base (excl. tax + affiliate)" value={moneyD(data.revenue_for_reserve)} indent />
                  <Row label="Sales tax retained (FL DOR)" value={moneyD(data.sales_tax_reserve)} color="text-orange-300" testid="bd-tax-reserve" />
                  <Row label={`Federal tax savings (${Math.round((data.federal_reserve_rate || 0.25) * 100)}%)`} value={moneyD(data.federal_reserve)} color="text-orange-300" testid="bd-fed-reserve" />
                  {data.already_reserved_processed > 0 && (
                    <Row label="Already deducted from processed payouts" value={moneyD(data.already_reserved_processed)} indent sub color="text-emerald-300/70" />
                  )}
                  {data.needs_review_amount > 0 && <Row label="Needs Review hold" value={moneyD(data.needs_review_amount)} color="text-red-300" />}
                  <Row label="Available for payout" value={moneyD(data.available_for_payout)} bold color="text-emerald-300" testid="bd-available" />
                </div>

                {/* Reconciliation */}
                <div className="mt-2 pt-2 border-t border-[#FFD166]/10 text-[10px] text-[#B9C8D5]">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {data.reconciliation.balanced
                      ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /> <span className="text-emerald-400">Reconciliation balanced</span></>
                      : <><AlertTriangle className="w-3 h-3 text-amber-400" /> <span className="text-amber-400">Reconciliation gap detected</span></>
                    }
                  </div>
                  <div className="bg-black/20 rounded-lg p-2 space-y-0.5 font-mono">
                    <div className="flex justify-between"><span>Net collected</span><span className="text-[#FFD166]">{moneyD(data.reconciliation.net_collected)}</span></div>
                    <div className="text-[#B9C8D5]/50 text-[9px] pl-2 space-y-0.5">
                      <div className="flex justify-between"><span>Sales tax retained</span><span className="text-orange-300">{moneyD(data.reconciliation.breakdown.sales_tax)}</span></div>
                      <div className="flex justify-between"><span>Federal reserve</span><span className="text-orange-300">{moneyD(data.reconciliation.breakdown.federal_reserve)}</span></div>
                      {data.reconciliation.breakdown.affiliate_commission > 0 && <div className="flex justify-between"><span>Affiliate reserved</span><span className="text-purple-300">{moneyD(data.reconciliation.breakdown.affiliate_commission)}</span></div>}
                      {data.reconciliation.breakdown.refunds > 0 && <div className="flex justify-between"><span>Refunded</span><span className="text-red-300">{moneyD(data.reconciliation.breakdown.refunds)}</span></div>}
                      <div className="flex justify-between"><span>Paid to bank</span><span className="text-emerald-300">{moneyD(data.reconciliation.breakdown.paid_to_bank)}</span></div>
                      <div className="flex justify-between"><span>Scheduled (pending)</span><span className="text-sky-300">{moneyD(data.reconciliation.breakdown.scheduled_pending)}</span></div>
                      {data.reconciliation.breakdown.unscheduled_available > 0.01 && <div className="flex justify-between"><span>Available (unscheduled)</span><span className="text-amber-300">{moneyD(data.reconciliation.breakdown.unscheduled_available)}</span></div>}
                    </div>
                    <div className="flex justify-between border-t border-[#FFD166]/10 pt-1">
                      <span>Total accounted</span>
                      <span className={data.reconciliation.balanced ? "text-emerald-400" : "text-amber-400"}>{moneyD(data.reconciliation.check_sum)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};


/* ── Communication Log section (collapsible) ── */
const CommLogSection = ({ reservationId }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-6" data-testid="comm-log-section">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="eyebrow mb-2 flex items-center gap-2 cursor-pointer hover:text-[#FFD166] transition-colors"
      >
        Communication Log
        <span className="text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && <CommunicationLog reservationId={reservationId} />}
    </div>
  );
};


const ActivityTimeline = ({ reservationId, reloadKey = 0 }) => {
  const [state, setState] = React.useState({ loading: true, error: null, data: null });
  React.useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/reservations/${reservationId}/activity`)
      .then((r) => { if (alive) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => { if (alive) setState({ loading: false, error: fmtErr(e.response?.data?.detail) || "Couldn't load activity", data: null }); });
    return () => { alive = false; };
  }, [reservationId, reloadKey]);

  if (state.loading) return (
    <div className="mt-6" data-testid="res-activity-loading">
      <div className="eyebrow mb-2">Payment history & activity</div>
      <div className="text-xs text-[#B9C8D5]">Loading…</div>
    </div>
  );
  if (state.error) return (
    <div className="mt-6" data-testid="res-activity-error">
      <div className="eyebrow mb-2">Payment history & activity</div>
      <div className="text-xs text-rose-300">{state.error}</div>
    </div>
  );
  const { summary = {}, items = [] } = state.data || {};
  const charged = Number(summary.charged_total || 0);
  const refunded = Number(summary.refunded_total || 0);
  const net = Number(summary.net || charged - refunded);

  return (
    <div className="mt-6" data-testid="res-activity">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">Payment history & activity</div>
        <div className="text-[10px] text-[#B9C8D5]">{items.length} event{items.length === 1 ? "" : "s"}</div>
      </div>

      {/* Financial rollup */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-2 text-center">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80">Charged</div>
          <div className="font-serif text-lg text-emerald-200" data-testid="res-activity-charged">${charged.toFixed(2)}</div>
        </div>
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-2 text-center">
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/80">Refunded</div>
          <div className="font-serif text-lg text-amber-200" data-testid="res-activity-refunded">${refunded.toFixed(2)}</div>
        </div>
        <div className="rounded-lg border border-[#FFD166]/25 bg-black/25 p-2 text-center">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#B9C8D5]">Net</div>
          <div className="font-serif text-lg text-[#FFD166]" data-testid="res-activity-net">${net.toFixed(2)}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-[#B9C8D5]" data-testid="res-activity-empty">No activity yet.</div>
      ) : (
        <ol className="space-y-2" data-testid="res-activity-list">
          {items.map((h, i) => {
            const style = ACTIVITY_TYPE_STYLES[h.type] || ACTIVITY_TYPE_STYLES.info;
            const Icon = style.icon;
            const amount = Number(h.amount || 0);
            const showAmt = amount !== 0 && !["change","info","delivery","payout_cancelled"].includes(h.type);
            return (
              <li key={`${h.at || i}-${h.type}-${i}`} className={`flex gap-3 p-2 rounded-lg border ${style.ring}`} data-testid={`res-activity-item-${i}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className={`text-sm ${style.color} truncate`}>{h.title}</div>
                    {showAmt && (
                      <div className={`text-sm font-mono shrink-0 ${style.color}`}>
                        {style.sign}${Math.abs(amount).toFixed(2)}
                      </div>
                    )}
                  </div>
                  {h.detail && <div className="text-[11px] text-[#B9C8D5] mt-0.5 break-words">{h.detail}</div>}
                  <div className="text-[10px] text-[#B9C8D5]/70 mt-0.5">{_fmtWhen(h.at)}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

/** Sync Reservation to Google Calendar button */
const SyncCalendarBtn = ({ reservation, onDone }) => {
  const [syncing, setSyncing] = React.useState(false);
  const hasCal = !!(reservation?.calendar_event_ids?.delivery_event_id);
  const handle = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/calendar-settings/sync/${reservation.id}`);
      toast.success(hasCal ? "Calendar events re-synced" : "Added to Google Calendar");
      onDone?.();
    } catch (e) {
      const msg = e.response?.data?.detail || "Calendar sync failed";
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };
  return (
    <Btn variant="ghost" onClick={handle} disabled={syncing} data-testid="res-detail-sync-calendar-btn" className="col-span-2">
      <Calendar className="w-4 h-4 mr-1" />
      {syncing ? "Syncing…" : hasCal ? "Re-sync to Google Calendar" : "Add to Google Calendar"}
    </Btn>
  );
};

// ── Tags tab panel ──────────────────────────────────────────────────────────
const ReservationTagsPanel = ({ reservation }) => {
  const [tags, setTags] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [orientation, setOrientation] = React.useState("vertical");
  // selection: Set of tag keys "{product_id}:{unit_number}"
  const [selected, setSelected] = React.useState(new Set());
  // quantities: map of tag key → copy count
  const [quantities, setQuantities] = React.useState({});
  const [printing, setPrinting] = React.useState(false);

  React.useEffect(() => {
    if (!reservation?.id) return;
    setLoading(true);
    axios.get(`${API}/admin/customer-tags`, { params: { reservation_id: reservation.id } })
      .then(({ data }) => {
        const t = data.tags || [];
        setTags(t);
        // Select all by default
        setSelected(new Set(t.map(tag => `${tag.product_id}:${tag.unit_number}`)));
      })
      .catch(() => toast.error("Couldn't load tags."))
      .finally(() => setLoading(false));
  }, [reservation?.id]);

  const tagKey = (tag) => `${tag.product_id}:${tag.unit_number}`;

  const allSelected = tags.length > 0 && tags.every(t => selected.has(tagKey(t)));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(tags.map(tagKey)));
  };
  const toggleOne = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const setQty = (key, val) => {
    const n = Math.max(1, Math.min(10, parseInt(val, 10) || 1));
    setQuantities(prev => ({ ...prev, [key]: n }));
  };

  const selectedTags = tags.filter(t => selected.has(tagKey(t)));

  const doPrint = async () => {
    if (!selectedTags.length) { toast.warning("Select at least one tag."); return; }
    setPrinting(true);
    try {
      // Build quantities map keyed by "{reservation_id}:{product_id}:{unit_number}"
      const qtys = {};
      selectedTags.forEach(t => {
        const k = `${t.reservation_id}:${t.product_id}:${t.unit_number}`;
        qtys[k] = quantities[tagKey(t)] || 1;
      });
      const resp = await axios.post(
        `${API}/admin/labels-pdf`,
        { tags: selectedTags, orientation, quantities: qtys },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed.");
    } finally {
      setPrinting(false);
    }
  };

  if (loading) return <div className="py-10 text-center text-[#B9C8D5] text-sm" data-testid="res-tags-loading">Loading tags…</div>;

  if (!tags.length) return (
    <div className="py-10 text-center text-[#B9C8D5] text-sm" data-testid="res-tags-empty">
      No printable tags — reservation must be paid and active.
    </div>
  );

  return (
    <div className="mt-4 space-y-4" data-testid="res-tags-panel">
      {/* Orientation toggle */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#B9C8D5] font-medium">Format:</span>
        <button
          type="button"
          onClick={() => setOrientation("vertical")}
          className={`px-3 py-1 rounded-full text-xs border transition-colors ${orientation === "vertical" ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
          data-testid="res-tags-orientation-vertical"
        >
          Vertical (tall)
        </button>
        <button
          type="button"
          onClick={() => setOrientation("horizontal")}
          className={`px-3 py-1 rounded-full text-xs border transition-colors ${orientation === "horizontal" ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
          data-testid="res-tags-orientation-horizontal"
        >
          Horizontal (62×38mm)
        </button>
      </div>

      {/* Select All row */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-[#FFD166] hover:underline"
          data-testid="res-tags-select-all"
        >
          {allSelected ? "Deselect all" : "Select all"}
        </button>
        <span className="text-xs text-[#B9C8D5]">{selectedTags.length} / {tags.length} selected</span>
      </div>

      {/* Tag list */}
      <div className="space-y-2">
        {tags.map((tag) => {
          const key = tagKey(tag);
          const checked = selected.has(key);
          return (
            <div
              key={key}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${checked ? "border-[#FFD166]/40 bg-[#FFD166]/5" : "border-[#B9C8D5]/15 bg-white/3"}`}
              data-testid={`res-tag-row-${key}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleOne(key)}
                className="w-4 h-4 accent-[#FFD166] cursor-pointer shrink-0"
                data-testid={`res-tag-check-${key}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#F6F1E4] truncate">{tag.product_name}</div>
                <div className="text-xs text-[#B9C8D5]">Unit {tag.unit_number}/{tag.unit_total}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-[#B9C8D5]">×</span>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={quantities[key] || 1}
                  onChange={(e) => setQty(key, e.target.value)}
                  disabled={!checked}
                  className="w-12 px-1.5 py-1 rounded bg-black/30 border border-[#B9C8D5]/20 text-xs text-center text-[#F6F1E4] disabled:opacity-40"
                  data-testid={`res-tag-qty-${key}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Print button */}
      <Btn
        onClick={doPrint}
        disabled={printing || !selectedTags.length}
        className="w-full"
        data-testid="res-tags-print-btn"
      >
        <Printer className="w-4 h-4" />
        {printing ? "Generating PDF…" : `Print ${selectedTags.length} tag${selectedTags.length === 1 ? "" : "s"} as PDF`}
      </Btn>
      <p className="text-[11px] text-[#B9C8D5] text-center leading-snug">
        iPhone: tap PDF → Share → Print → select QL-810W<br />
        × column sets how many copies of each tag to print
      </p>
    </div>
  );
};


const ReservationDetail = ({ reservation: initialReservation, productsById, onClose, onCancel, onExtraCharge, onEdit, onReload }) => {
  const [r, setR] = React.useState(initialReservation);
  React.useEffect(() => { setR(initialReservation); }, [initialReservation]);
  const [skipReview, setSkipReview] = React.useState(!!initialReservation.skip_review_email);
  const [refundOpen, setRefundOpen] = React.useState(false);
  const [promoOpen, setPromoOpen] = React.useState(false);
  const [msgsOpen, setMsgsOpen] = React.useState(false);
  const [drawerTab, setDrawerTab] = React.useState("details"); // "details" | "messages" | "activity" | "finances"
  const [reloadKey, setReloadKey] = React.useState(0);
  const fmtWhen = (x) => new Date(x).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });

  // Refetch this specific reservation's fresh data (refund totals, promo code
  // stamp, history) so the drawer reflects reality immediately after a
  // manual refund / apply-promo action instead of showing the pre-action
  // snapshot until the admin closes and re-opens the drawer.
  const refreshThisReservation = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reservations/${initialReservation.id}`);
      if (data) setR(data);
    } catch (_) { toast.warning("Saved, but the drawer couldn't refresh — reopen it to see the latest."); }
    setReloadKey((k) => k + 1);
    onReload && onReload();
  }, [initialReservation.id, onReload]);
  const toggleSkip = async () => {
    const next = !skipReview;
    try {
      await axios.patch(`${API}/admin/reservations/${r.id}/review-email`, { skip: next });
      setSkipReview(next);
      toast.success(next ? "Review email will be skipped." : "Review email re-enabled.");
      onReload?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const [startUnitWarn, setStartUnitWarn] = React.useState(false);
  const [resendingEmail, setResendingEmail] = React.useState(false);
  const [togglingTest, setTogglingTest] = React.useState(false);
  const [permDeleting, setPermDeleting] = React.useState(false);

  const toggleTestOrder = async () => {
    const next = !r.is_test_order;
    const msg = next
      ? "Mark this as a TEST ORDER? It will be excluded from analytics, revenue, tax, and payout reporting. The reservation remains fully operational."
      : "Remove the TEST ORDER flag? This reservation will re-appear in all analytics and reporting.";
    if (!window.confirm(msg)) return;
    setTogglingTest(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/toggle-test-order`);
      toast.success(data.is_test_order ? "Marked as Test Order — excluded from analytics." : "Test Order flag removed — now included in analytics.");
      refreshThisReservation();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setTogglingTest(false); }
  };

  const permanentDelete = async () => {
    if (!window.confirm(
      `PERMANENTLY DELETE reservation ${r.short_code || r.id.slice(0, 8).toUpperCase()}?\n\n` +
      `This will:\n• Release inventory units\n• Cancel pending payouts\n• Reverse any promo redemption\n• Delete Google Calendar events\n• Remove all comm logs\n\n` +
      `Stripe financial history is NOT deleted.\n\nThis CANNOT be undone. Type DELETE in the next prompt to confirm.`
    )) return;
    const typed = window.prompt('Type DELETE (all caps) to permanently delete this reservation:');
    if (typed?.trim() !== "DELETE") { toast.info("Cancelled — you must type DELETE exactly."); return; }
    setPermDeleting(true);
    try {
      await axios.post(`${API}/admin/reservations/${r.id}/permanent-delete`, { confirm: "DELETE" });
      toast.success("Reservation permanently deleted and all related records cleaned up.");
      onClose();
      onReload?.();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Permanent delete blocked — check payment verification.");
    } finally { setPermDeleting(false); }
  };

  const resendConfirmationEmail = async () => {
    if (!window.confirm("Re-send the booking confirmation email to the customer AND a new booking alert to the admin? This will override the sent flag and deliver fresh copies.")) return;
    setResendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/resend-confirmation`);
      if (data.customer_email_sent) {
        toast.success(`Confirmation emails re-sent ✓\nCustomer: ${data.customer_email}\nAdmin: ${data.admin_email || "not configured"}`);
      } else {
        toast.warning("Email was triggered but the send flag wasn't set — check backend logs.");
      }
      refreshThisReservation();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend confirmation emails");
    } finally {
      setResendingEmail(false);
    }
  };

  const startOrder = async (override = false) => {
    const hasUnits = (r.assigned_units || []).length > 0;
    if (!hasUnits && !override) {
      setStartUnitWarn(true);
      return;
    }
    if (!window.confirm("Start this order? This marks the gear as delivered / out with the guest.")) return;
    try {
      await axios.post(`${API}/admin/reservations/${r.id}/start`, {});
      toast.success("Order started ✓");
      setStartUnitWarn(false);
      refreshThisReservation();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const returnOrder = async () => {
    if (!window.confirm("Mark everything returned and complete this order?")) return;
    try {
      await axios.post(`${API}/admin/reservations/${r.id}/return`);
      toast.success("Marked returned ✓");
      refreshThisReservation();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
  <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-end p-4" onClick={onClose} data-testid="res-detail-drawer">
    <div className="glass w-full max-w-xl max-h-[92vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="res-detail-close"><XIcon className="w-5 h-5" /></button>
        <p className="eyebrow text-[#FFD166]">Reservation</p>
      <h2 className="font-serif text-3xl mt-1" data-testid="res-detail-customer">{r.customer_name}</h2>
      <div className="mt-1 text-xs text-[#B9C8D5]">
        {r.short_code && <span className="text-[#FFD166] font-mono mr-2">{r.short_code}</span>}
        <span className="font-mono">{r.id}</span>
      </div>
      {/* Test Order badge */}
      {r.is_test_order && (
        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs" data-testid="res-detail-test-badge">
          <FlaskConical className="w-3.5 h-3.5" /> Test Order — excluded from analytics &amp; reporting
        </div>
      )}
      {/* Calendar sync warning */}
      {r.calendar_sync_status === "sync_failed" && (
        <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs" data-testid="res-detail-cal-sync-warning">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Calendar auto-sync failed: {r.calendar_sync_error || "unknown error"}. Use Re-sync to Google Calendar to repair.</span>
        </div>
      )}
      {/* Payment mismatch warning */}
      {r.payment_mismatch && (
        <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-xs" data-testid="res-detail-mismatch-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>PAYMENT MISMATCH: Internal status is unpaid but Stripe confirms real payment. Admin review required before any deletion.</span>
        </div>
      )}

      {/* Drawer Tab Bar */}
      <div className="mt-5 flex border-b border-[#FFD166]/15 overflow-x-auto" data-testid="res-drawer-tabs">
        {[
          { id: "details", label: "Details" },
          { id: "messages", label: "Messages" },
          { id: "activity", label: "Activity" },
          { id: "finances", label: "Finances" },
          { id: "tags", label: "Tags" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setDrawerTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
              drawerTab === tab.id
                ? "border-[#FFD166] text-[#FFD166]"
                : "border-transparent text-[#B9C8D5] hover:text-[#F6F1E4]"
            }`}
            data-testid={`res-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Messages tab ────────────────────────────────────────────── */}
      {drawerTab === "messages" && (
        <div className="mt-4 h-[65vh] flex flex-col" data-testid="res-messages-tab">
          <ConversationPanel
            inline
            reservationId={r.id}
            customerName={r.customer_name}
          />
        </div>
      )}

      {/* ── Activity tab ────────────────────────────────────────────── */}
      {drawerTab === "activity" && (
        <div data-testid="res-activity-tab">
          <ActivityTimeline reservationId={r.id} reloadKey={reloadKey} />
        </div>
      )}

      {/* ── Finances tab ────────────────────────────────────────────── */}
      {drawerTab === "finances" && (
        <div data-testid="res-finances-tab">
          <NeedsReviewSection reservationId={r.id} onResolved={refreshThisReservation} />
          <FinancialBreakdownSection reservationId={r.id} reloadKey={reloadKey} />
        </div>
      )}

      {/* ── Tags tab ─────────────────────────────────────────────────── */}
      {drawerTab === "tags" && (
        <div data-testid="res-tags-tab">
          <ReservationTagsPanel reservation={r} />
        </div>
      )}

      {/* ── Details tab (default) ───────────────────────────────────── */}
      {drawerTab === "details" && (<>

      <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="eyebrow mb-1">Email</div>
          <a href={`mailto:${r.customer_email}`} className="text-[#FFD166] hover:underline break-all">{r.customer_email}</a>
        </div>
        <div>
          <div className="eyebrow mb-1">Phone</div>
          {r.customer_phone ? <a href={`tel:${r.customer_phone}`} className="text-[#FFD166] hover:underline">{r.customer_phone}</a> : <span className="text-[#B9C8D5]">—</span>}
          {r.sms_opted_in && <div className="text-[10px] text-[#7EE787] mt-0.5">✓ opted in to SMS</div>}
        </div>
        <div className="col-span-2">
          <div className="eyebrow mb-1">Destination</div>
          <div>{r.delivery_resort}</div>
        </div>
        <div>
          <div className="eyebrow mb-1">Delivery</div>
          <div>{r.delivery_date}</div>
          <div className="text-xs text-[#B9C8D5]">{formatSlot(r.delivery_slot)}</div>
        </div>
        <div>
          <div className="eyebrow mb-1">Pickup</div>
          <div>{r.pickup_date}</div>
          <div className="text-xs text-[#B9C8D5]">{formatSlot(r.pickup_slot)}</div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-[#FFD166]/15 bg-black/25 p-3" data-testid="res-comm-status">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="text-xs space-y-1">
            <div className="eyebrow">Communication</div>
            <div className={r.sms_opted_in ? "text-emerald-300" : "text-[#F4A93A]"} data-testid="res-comm-sms-status">
              {r.sms_opted_in ? "✓ SMS opted in" : "✗ SMS not opted in — email only"}
              {r.sms_consent_at && (
                <span className="text-[#B9C8D5]"> · {fmtWhen(r.sms_consent_at)} ({r.sms_consent_source === "website_checkout" ? "website checkout" : r.sms_consent_source || "manual"})</span>
              )}
            </div>
            <div className="text-[#B9C8D5]">Email {r.customer_email ? "available" : "not on file"}</div>
            <div className={r.confirmation_email_sent ? "text-emerald-300" : "text-amber-300"} data-testid="res-confirmation-email-status">
              {r.confirmation_email_sent ? "✓ Confirmation email sent" : "✗ Confirmation email not yet sent"}
            </div>
            {r.comm_last_outbound_at && <div className="text-[#B9C8D5]">Last outbound · {fmtWhen(r.comm_last_outbound_at)}</div>}
            {r.comm_last_inbound_at && <div className="text-[#B9C8D5]">Last customer reply · {fmtWhen(r.comm_last_inbound_at)}</div>}
          </div>
          <button
            type="button"
            onClick={() => setMsgsOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FFD166]/50 bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166]/20 text-sm font-medium"
            data-testid="res-open-messages"
          >
            💬 Messages
          </button>
        </div>
      </div>
      {msgsOpen && <ConversationPanel reservationId={r.id} customerName={r.customer_name} onClose={() => { setMsgsOpen(false); refreshThisReservation(); }} />}

      <div className="mt-6">
        <div className="eyebrow mb-3">Gear ({(r.line_items || []).length} lines)</div>
        <div className="space-y-2" data-testid="res-detail-gear">
          {(r.line_items || []).map((li) => {
            const p = productsById[li.product_id];
            return (
              <div key={li.product_id} className="flex items-start gap-3 p-2 rounded-lg bg-black/30 border border-[#FFD166]/10">
                {p?.image ? <img src={p.image.startsWith("/") ? `${BACKEND_URL}${p.image}` : p.image} alt={li.name} className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-[#FFD166]/10" />}
                <div className="flex-1">
                  <div className="font-medium">{li.name}</div>
                  <div className="text-xs text-[#B9C8D5]">× {li.quantity} · ${li.subtotal}</div>
                  {/* Staged serials */}
                  {(li.staged_serials || []).some(Boolean) && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {Array.from({ length: li.quantity }).map((_, idx) => {
                        const serial = (li.staged_serials || [])[idx];
                        return serial ? (
                          <span key={idx} data-testid={`staged-serial-${li.product_id}-${idx}`} className="inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full font-mono">
                            <CheckCircle2 className="w-2.5 h-2.5 flex-shrink-0" />Staged · {serial}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PhotoStrip reservation={r} onChange={onReload} />

      <div className="mt-6 pt-4 border-t border-[#FFD166]/15 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Subtotal</span><span>${Number(r.subtotal || 0).toFixed(2)}</span></div>
        {Number(r.promo_discount || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>{r.partner_code ? `Partner discount (${r.partner_code})` : `Promo code (${r.promo_code})`}</span><span>−${Number(r.promo_discount).toFixed(2)}</span></div>}
        {r.partner_code && (
          <div className="mt-3 p-3 rounded-xl border border-purple-500/20 bg-purple-500/5" data-testid="res-affiliate-section">
            <p className="text-[10px] uppercase tracking-wider text-purple-300 mb-2">Affiliate Commission</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-[#B9C8D5]">Partner</span><span className="text-[#F6F1E4]">{r.partner_name || r.partner_code}</span></div>
              <div className="flex justify-between"><span className="text-[#B9C8D5]">Referral code</span><span className="font-mono text-purple-300">{r.partner_code}</span></div>
              {r.partner_commission_percent > 0 && (
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Commission rate</span><span className="text-purple-300">{Number(r.partner_commission_percent).toFixed(1)}% of {r.partner_commission_basis || "subtotal"}</span></div>
              )}
              {r.partner_commissionable_subtotal > 0 && (
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Commission base</span><span className="font-mono text-purple-300">${Number(r.partner_commissionable_subtotal).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between border-t border-purple-500/10 pt-1">
                <span className="text-[#B9C8D5]">Commission amount</span>
                <span className="font-mono text-purple-300 font-medium">${Number(r.partner_commission || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#B9C8D5]">Status</span>
                <span className={`capitalize font-medium ${r.partner_commission_paid ? "text-emerald-300" : "text-amber-300"}`}>
                  {r.partner_commission_paid ? "Paid" : "Pending / reserved"}
                </span>
              </div>
              {r.partner_commission_paid_at && (
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Paid on</span><span className="text-[#F6F1E4]">{r.partner_commission_paid_at?.slice(0,10)}</span></div>
              )}
              {r.partner_payout_id && (
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Reference</span><span className="font-mono text-xs text-[#B9C8D5]">{r.partner_payout_id}</span></div>
              )}
              {!r.partner_commission_paid && (
                <p className="text-[10px] text-purple-300/60 mt-1 italic">Commission reserved from sweeps until paid or reversed.</p>
              )}
            </div>
          </div>
        )}
        {Number(r.auto_promo_discount || 0) > 0 && <div className="flex justify-between text-[#FFD166]" data-testid="res-detail-auto-promo"><span>🎁 {r.applied_promo?.checkout_label || r.applied_promo?.title || "Promotion"}</span><span>−${Number(r.auto_promo_discount).toFixed(2)}</span></div>}
        {Number(r.byob_discount || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>Build-Your-Own Bundle</span><span>−${Number(r.byob_discount).toFixed(2)}</span></div>}
        {(Number(r.promo_discount || 0) + Number(r.auto_promo_discount || 0) + Number(r.byob_discount || 0)) > 0 && (
          <div className="flex justify-between border-t border-[#FFD166]/10 pt-1" data-testid="res-detail-taxable-subtotal"><span className="text-[#B9C8D5]">Taxable subtotal</span><span>${Number(r.taxable_subtotal ?? Math.max(0, Number(r.subtotal || 0) - Number(r.promo_discount || 0) - Number(r.auto_promo_discount || 0) - Number(r.byob_discount || 0))).toFixed(2)}</span></div>
        )}
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Delivery</span><span>${Number(r.delivery_fee || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Tax ({((r.tax_rate || 0) * 100).toFixed(1)}%)</span><span>${Number(r.tax || 0).toFixed(2)}</span></div>
        {Number(r.enchanted_care_fee || 0) > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Enchanted Care</span><span>+${Number(r.enchanted_care_fee).toFixed(2)}</span></div>}
        {Number(r.enchanted_pom_fee || 0) > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Peace of Mind</span><span>+${Number(r.enchanted_pom_fee).toFixed(2)}</span></div>}
        {Number(r.tip_amount || 0) > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Tip</span><span>+${Number(r.tip_amount).toFixed(2)}</span></div>}
        <div className="flex justify-between text-lg pt-1"><span className="text-[#FFD166]">Total</span><span className="text-[#FFD166] font-serif">${Number(r.total).toFixed(2)}</span></div>
        {r.charged_total != null && Math.abs(Number(r.charged_total) - Number(r.total || 0)) > 0.01 && (
          <div className="flex justify-between text-red-300 text-xs" data-testid="res-detail-charge-mismatch"><span>⚠️ Stripe actually charged</span><span>${Number(r.charged_total).toFixed(2)}</span></div>
        )}
      </div>

      {r.card_last4 && (
        <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 flex items-center gap-2 text-sm" data-testid="res-detail-card-on-file">
          <CreditCard className="w-4 h-4 text-emerald-300" />
          <span className="text-[#F6F1E4]">Card on file · <b className="text-emerald-200">{(r.card_brand || "card").toUpperCase()} ••••{r.card_last4}</b></span>
          <span className="ml-auto text-[10px] text-[#B9C8D5]">Silent-charge ready</span>
        </div>
      )}

      {r.notes && (<div className="mt-6"><div className="eyebrow mb-1">Notes</div><div className="text-sm text-[#F6F1E4]/90 whitespace-pre-line">{r.notes}</div></div>)}

      <AgreementsPanel r={r} />

      {/* Phase 3: No unit assignment warning modal */}
      {startUnitWarn && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={() => setStartUnitWarn(false)}>
          <div className="glass max-w-sm w-full p-6 rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="unit-warn-modal">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300">No inventory units assigned</div>
            <p className="text-sm text-[#B9C8D5] mt-2">
              No physical inventory units have been scanned or assigned to this reservation yet.
              Starting without unit assignment means you won't be able to track which physical units are out.
            </p>
            <div className="mt-4 flex gap-3">
              <button className="flex-1 px-4 py-2 rounded-lg border border-[#FFD166]/30 text-[#FFD166] text-sm hover:bg-[#FFD166]/10" onClick={() => setStartUnitWarn(false)} data-testid="unit-warn-cancel">Cancel — assign units first</button>
              <button className="flex-1 px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/50 text-amber-300 text-sm hover:bg-amber-500/30" onClick={() => { setStartUnitWarn(false); startOrder(true); }} data-testid="unit-warn-override">Start anyway</button>
            </div>
          </div>
        </div>
      )}

      {r.status !== "cancelled" && r.payment_status === "paid" && (
        <div className="mt-6 space-y-2" data-testid="res-detail-actions">
          {/* Order lifecycle — Start (gear goes out) → Return (order complete) */}
          {!hasStarted(r) ? (
            <>
              {isLateStart(r) && (
                <div className="p-2.5 rounded-lg border border-red-500/50 bg-red-500/10 text-xs text-red-300 flex items-center gap-2" data-testid="res-detail-late-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Past the delivery window ({r.delivery_date} · {formatSlot(r.delivery_slot)}) and this order hasn't been started yet.
                </div>
              )}
              <Btn onClick={() => startOrder()} className="w-full" data-testid="res-detail-start-btn">
                <Truck className="w-4 h-4" /> Start order — mark delivered
              </Btn>
            </>
          ) : r.status !== "completed" ? (
            <>
              <div className="p-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-xs text-emerald-300 flex items-center gap-2" data-testid="res-detail-started-info">
                <Truck className="w-4 h-4 shrink-0" />
                Started {fmtWhenET(r.delivered_at)}{r.delivered_by ? ` · by ${r.delivered_by}` : ""}
              </div>
              <Btn onClick={returnOrder} className="w-full" data-testid="res-detail-return-btn">
                <Check className="w-4 h-4" /> Mark returned — complete order
              </Btn>
            </>
          ) : (
            <div className="p-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-xs text-emerald-300 flex items-center gap-2" data-testid="res-detail-completed-info">
              <Check className="w-4 h-4 shrink-0" /> Returned — order complete
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Btn variant="ghost" onClick={() => onEdit(r)} data-testid="res-detail-edit-btn">✎ Edit booking</Btn>
            <Btn variant="ghost" onClick={() => onExtraCharge(r)} data-testid="res-detail-extra-btn">💳 Extra charge</Btn>
            <Btn variant="ghost" onClick={() => setRefundOpen(true)} data-testid="res-detail-manual-refund-btn"><Banknote className="w-4 h-4" /> Manual refund</Btn>
            <Btn variant="ghost" onClick={() => setPromoOpen(true)} disabled={!!r.promo_code} title={r.promo_code ? `Code ${r.promo_code} already applied` : undefined} data-testid="res-detail-apply-promo-btn">🎟️ Apply promo code</Btn>
            <SyncCalendarBtn reservation={r} onDone={refreshThisReservation} />
            <Btn variant="ghost" onClick={resendConfirmationEmail} disabled={resendingEmail} data-testid="res-detail-resend-email-btn">
              <Mail className="w-4 h-4" /> {resendingEmail ? "Sending…" : "Resend confirmation"}
            </Btn>
            {/* Test Order toggle */}
            <Btn
              variant="ghost"
              onClick={toggleTestOrder}
              disabled={togglingTest}
              data-testid="res-detail-toggle-test-btn"
              className={`col-span-2 ${r.is_test_order ? "text-violet-300 border-violet-500/40" : "text-[#B9C8D5]"}`}
            >
              <FlaskConical className="w-4 h-4 mr-1" />
              {togglingTest ? "Updating…" : r.is_test_order ? "Remove Test Order flag" : "Mark as Test Order"}
            </Btn>
            {/* Permanent delete — only for unpaid or test orders */}
            {(r.payment_status !== "paid" || r.is_test_order) && (
              <Btn
                variant="ghost"
                onClick={permanentDelete}
                disabled={permDeleting}
                data-testid="res-detail-perm-delete-btn"
                className="col-span-2 text-red-400 border-red-500/30 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {permDeleting ? "Verifying &amp; deleting…" : "Delete Permanently (with safety check)"}
              </Btn>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-[#F6F1E4] p-2 rounded border border-[#FFD166]/15 bg-black/25 cursor-pointer" data-testid="res-detail-skip-review-wrap">
            <input type="checkbox" checked={skipReview} onChange={toggleSkip} className="accent-[#FFD166]" data-testid="res-detail-skip-review" />
            Skip post-stay review email (customer had a rough experience)
          </label>
          <Btn variant="danger" onClick={() => onCancel(r)} data-testid="res-detail-cancel-btn" className="w-full"><XIcon className="w-4 h-4" /> Cancel &amp; refund this reservation</Btn>
        </div>
      )}
      </>)}
    </div>
    {refundOpen && <ManualRefundModal reservation={r} onClose={() => setRefundOpen(false)} onDone={() => { setRefundOpen(false); refreshThisReservation(); }} />}
    {promoOpen && <ApplyPromoCodeModal reservation={r} onClose={() => setPromoOpen(false)} onDone={() => { setPromoOpen(false); refreshThisReservation(); }} />}
  </div>
  );
};

/** Expired Unpaid Cleanup Modal — preview then confirm deletion */
const ExpiredUnpaidCleanupModal = ({ onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [runReport, setRunReport] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setReport(null);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/cleanup/expired-unpaid/preview`);
      setReport(data);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const runCleanup = async () => {
    if (!report || report.deletion_summary.would_delete === 0) {
      toast.info("Nothing eligible to delete.");
      return;
    }
    if (!window.confirm(
      `About to PERMANENTLY DELETE ${report.deletion_summary.would_delete} expired unpaid reservation(s).\n\n` +
      `Each will be verified against Stripe before deletion. This cannot be undone.\n\nProceed?`
    )) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/cleanup/expired-unpaid/run`, { confirm: "DELETE_EXPIRED_UNPAID" });
      setRunReport(data);
      toast.success(`Cleanup complete: ${data.actually_deleted} deleted, ${data.deletion_summary.protected} protected.`);
      onDone?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  React.useEffect(() => { runPreview(); }, []);

  const ReportSection = ({ title, items, color = "text-[#B9C8D5]", testId }) => (
    items?.length > 0 ? (
      <div className="mt-3" data-testid={testId}>
        <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${color}`}>{title} ({items.length})</div>
        <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
          {items.map((it, i) => (
            <div key={i} className="text-xs text-[#B9C8D5] bg-black/20 rounded px-2 py-1 flex justify-between gap-2">
              <span className="truncate">{it.customer_name || it.id}</span>
              <span className="shrink-0 text-[10px] opacity-60">{it.pickup_date || it.reason || it.message?.slice(0, 60)}</span>
            </div>
          ))}
        </div>
      </div>
    ) : null
  );

  const displayReport = runReport || report;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" type="button"><XIcon className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 mb-4">
          <CalendarX className="w-5 h-5 text-amber-300" />
          <h2 className="font-serif text-2xl text-[#FFD166]">Expired Unpaid Cleanup</h2>
        </div>
        <p className="text-sm text-[#B9C8D5] mb-4">
          Reviews all unpaid, non-cancelled reservations. Only permanently deletes those where:
          the rental has fully ended (before today in ET), <strong>AND</strong> Stripe conclusively confirms $0 collected.
          Protected records are listed but never touched.
        </p>

        {loading && <div className="text-center text-[#B9C8D5] py-8" data-testid="cleanup-loading">Running dry-run analysis…</div>}

        {displayReport && !loading && (
          <div data-testid="cleanup-report">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: "Total Unpaid", value: displayReport.total_unpaid, color: "text-[#F6F1E4]", testId: "cleanup-total-unpaid" },
                { label: runReport ? "Actually Deleted" : "Would Delete", value: runReport ? runReport.actually_deleted : displayReport.deletion_summary.would_delete, color: runReport ? "text-red-300" : "text-amber-300", testId: "cleanup-would-delete" },
                { label: "Protected", value: displayReport.deletion_summary.protected, color: "text-emerald-300", testId: "cleanup-protected" },
                { label: "Mismatches", value: displayReport.protected_payment_mismatch?.length || 0, color: "text-rose-300", testId: "cleanup-mismatches" },
              ].map(({ label, value, color, testId }) => (
                <div key={label} className="text-center p-3 rounded bg-black/25 border border-white/10" data-testid={testId}>
                  <div className={`text-xl font-mono ${color}`}>{value}</div>
                  <div className="text-[10px] text-[#B9C8D5] mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            <ReportSection title="Eligible for deletion" items={displayReport.eligible_for_deletion} color="text-amber-300" testId="cleanup-eligible-list" />
            <ReportSection title="Protected — future rental" items={displayReport.protected_future_rental} color="text-emerald-300" testId="cleanup-protected-future-list" />
            <ReportSection title="Protected — rental ends today (ET)" items={displayReport.protected_today_rental} color="text-emerald-300" testId="cleanup-protected-today-list" />
            <ReportSection title="Protected — Stripe payment confirmed" items={displayReport.protected_stripe_payment_found} color="text-sky-300" testId="cleanup-protected-stripe-list" />
            <ReportSection title="PAYMENT MISMATCH (Stripe shows payment, internal says unpaid)" items={displayReport.protected_payment_mismatch} color="text-rose-300" testId="cleanup-mismatch-list" />
            <ReportSection title="Protected — Stripe unreachable" items={displayReport.protected_stripe_unreachable} color="text-amber-300" testId="cleanup-protected-unreachable-list" />
            <ReportSection title="Protected — Stripe inconclusive" items={displayReport.protected_stripe_inconclusive} color="text-amber-300" testId="cleanup-protected-inconclusive-list" />
            <ReportSection title="Protected — other" items={displayReport.protected_other} testId="cleanup-protected-other-list" />

            {runReport?.delete_errors?.length > 0 && (
              <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded p-3">
                {runReport.delete_errors.length} deletion(s) failed — check backend logs.
              </div>
            )}

            <div className="text-[10px] text-[#B9C8D5]/60 mt-3">
              Evaluated at: {displayReport.evaluated_at_et}
            </div>
          </div>
        )}

        {!runReport && (
          <div className="flex gap-3 mt-4">
            <Btn variant="ghost" onClick={runPreview} disabled={loading} data-testid="cleanup-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> {loading ? "Analyzing…" : "Refresh preview"}
            </Btn>
            {report && report.deletion_summary.would_delete > 0 && (
              <Btn
                onClick={runCleanup}
                disabled={running}
                data-testid="cleanup-run-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {running ? "Running cleanup…" : `Delete ${report.deletion_summary.would_delete} eligible record(s)`}
              </Btn>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CancelRefundModal = ({ reservation, onClose, onDone }) => {
  const [quote, setQuote] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [reasonTag, setReasonTag] = useState("");
  const [busy, setBusy] = useState(false);
  // Reason-aware retention: when the admin picks "Plans changed" we offer a
  // reschedule instead of a refund. Suggested new dates default to the
  // reservation's current dates + 7 days (with a 3-day-min trip length).
  const shiftIso = (iso, days) => {
    if (!iso) return "";
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const [newDelivery, setNewDelivery] = useState(() => shiftIso(reservation.delivery_date, 7));
  const [newPickup, setNewPickup] = useState(() => shiftIso(reservation.pickup_date, 7));
  useEffect(() => {
    axios.get(`${API}/admin/reservations/${reservation.id}/refund-quote`)
      .then((r) => { setQuote(r.data); setAmount(String(r.data.suggested_refund ?? 0)); })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load refund quote"));
  }, [reservation.id]);

  const submit = async ({ skipRefund = false } = {}) => {
    setBusy(true);
    try {
      const body = {
        note,
        reason_tag: reasonTag || null,
        skip_refund: skipRefund,
        refund_amount: skipRefund ? null : Number(amount || 0),
      };
      const r = await axios.post(`${API}/admin/reservations/${reservation.id}/cancel`, body);
      const rf = r.data.refund_amount;
      if (r.data.refund_ignored_reason) {
        toast.warning(r.data.refund_ignored_reason);
      } else if (rf > 0) {
        toast.success(`Cancelled + refunded $${rf.toFixed(2)}`);
      } else {
        toast.success("Cancelled (no refund)");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const submitPom = async () => {
    if (!window.confirm("Process the full Enchanted Peace of Mind refund and cancel this reservation? Enchanted Care fee is kept.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/reservations/${reservation.id}/pom-refund`, { note, reason_tag: reasonTag || null });
      toast.success(`PoM refund $${(r.data.refund_amount || 0).toFixed(2)} · Care fee $${(r.data.care_kept || 0).toFixed(2)} kept`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "PoM refund failed");
    } finally { setBusy(false); }
  };

  const submitReschedule = async () => {
    if (!newDelivery || !newPickup) return toast.error("Pick both new delivery and pickup dates.");
    if (newPickup <= newDelivery) return toast.error("Pickup date must be after delivery date.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${reservation.id}/edit`, {
        delivery_date: newDelivery,
        pickup_date: newPickup,
      });
      // Fire a retention analytics event so we can measure how often this saves a trip.
      try {
        await axios.post(`${API}/analytics/track`, { event: "retention_reschedule", meta: { reservation_id: reservation.id, reason_tag: reasonTag } });
      } catch { /* silent */ }
      const notified = data?.reschedule_notify?.sent;
      const channel = data?.reschedule_notify?.channel;
      toast.success(
        notified
          ? `🌙 Trip rescheduled to ${newDelivery} → ${newPickup} · customer texted (${channel})`
          : `🌙 Trip saved — rescheduled to ${newDelivery} → ${newPickup}`
      );
      onDone();
    } catch (e) {
      const msg = e.response?.data?.detail;
      const items = e.response?.data?.detail?.shortages;
      if (items && items.length) {
        toast.error(`Those dates aren't available — ${items.length} item${items.length === 1 ? "" : "s"} sold out. Try different dates.`);
      } else {
        toast.error(typeof msg === "string" ? msg : "Reschedule failed");
      }
    } finally { setBusy(false); }
  };

  const policyKind = quote?.policy?.policy || "standard";
  const canPomRefund = !!quote && quote.enchanted_pom && !quote.pom_locked_by_edit && quote.max_refundable > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cancel-refund-modal">
      <div className="glass max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]"><XIcon className="w-5 h-5" /></button>
        <h2 className="font-serif text-2xl text-[#FFD166]">Cancel &amp; refund</h2>
        <div className="text-sm text-[#B9C8D5] mt-1">{reservation.customer_name} · {reservation.delivery_date}</div>

        {!quote ? (
          <div className="mt-6 text-[#B9C8D5]">Loading policy math…</div>
        ) : (
          <>
            {/* Policy badge */}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="refund-policy-badges">
              {policyKind === "pom" ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#FFD166]/50 bg-[#FFD166]/10 text-[#FFD166] px-3 py-1 text-[11px] uppercase tracking-wide" data-testid="badge-pom">✨ Peace of Mind</span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#B9C8D5]/30 bg-black/25 text-[#B9C8D5] px-3 py-1 text-[11px] uppercase tracking-wide" data-testid="badge-standard">Standard policy</span>
              )}
              {quote.enchanted_care && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-3 py-1 text-[11px] uppercase tracking-wide" data-testid="badge-care">Care · ${Number(quote.enchanted_care_fee || 0).toFixed(2)} kept</span>
              )}
              {quote.pom_locked_by_edit && (
                <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-1 text-[11px] uppercase tracking-wide" data-testid="badge-pom-locked">PoM locked (dates changed)</span>
              )}
            </div>

            <div className="mt-5 rounded-lg border border-[#FFD166]/20 bg-black/30 p-4 text-sm space-y-1" data-testid="refund-breakdown">
              <div className="flex justify-between"><span className="text-[#B9C8D5]">Rental subtotal</span><span>${(quote.subtotal || 0).toFixed(2)}</span></div>
              {quote.delivery_fee > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Delivery</span><span>${quote.delivery_fee.toFixed(2)}</span></div>}
              {quote.damage_protection_fee > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Damage Protection (legacy)</span><span>${quote.damage_protection_fee.toFixed(2)}</span></div>}
              {quote.enchanted_care_fee > 0 && <div className="flex justify-between text-emerald-300"><span>Enchanted Care (non-refundable)</span><span>${quote.enchanted_care_fee.toFixed(2)}</span></div>}
              {quote.enchanted_pom_fee > 0 && <div className="flex justify-between text-[#FFD166]"><span>Enchanted Peace of Mind</span><span>${quote.enchanted_pom_fee.toFixed(2)}</span></div>}
              {Number(quote.tip_amount || 0) > 0 && (
                <div className="flex justify-between text-emerald-300" data-testid="refund-tip-line">
                  <span>Tip {Number(quote.policy?.tip_kept || 0) > 0 ? "(kept — reservation started)" : "(refundable)"}</span>
                  <span>${Number(quote.tip_amount).toFixed(2)}</span>
                </div>
              )}
              {quote.tax > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Sales tax</span><span>${quote.tax.toFixed(2)}</span></div>}
              {quote.promo_discount > 0 && (
                <div className="flex justify-between text-emerald-300" data-testid="refund-promo-line">
                  <span>Coupon ({quote.promo_code}) — never paid</span>
                  <span>−${quote.promo_discount.toFixed(2)}</span>
                </div>
              )}
              {Number(quote.byob_discount || 0) > 0 && (
                <div className="flex justify-between text-emerald-300" data-testid="refund-byob-line">
                  <span>Build-Your-Own Bundle savings</span>
                  <span>−${Number(quote.byob_discount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-[#FFD166]/10 pt-2 mt-2 font-medium">
                <span className="text-[#F6F1E4]">Customer paid</span>
                <span className="text-[#F6F1E4]">${quote.total.toFixed(2)}</span>
              </div>
              {quote.already_refunded > 0 && <div className="flex justify-between text-[#B9C8D5]"><span>Already refunded</span><span>${quote.already_refunded.toFixed(2)}</span></div>}
              <div className="flex justify-between"><span className="text-[#B9C8D5]">Days out</span><span>{quote.policy.days_out ?? "—"}</span></div>
              <div className="flex justify-between border-t border-[#FFD166]/10 pt-2 mt-2">
                <span className="text-[#F6F1E4]">Policy says</span>
                <span className="text-[#FFD166]">{quote.policy.refund_percent}% · ${quote.suggested_refund.toFixed(2)}</span>
              </div>
              <div className="text-[11px] text-[#B9C8D5] mt-1">{quote.policy.policy_label}</div>
              {quote.promo_discount > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-500/30 text-[11px] text-emerald-300 leading-relaxed" data-testid="refund-coupon-notice">
                  <b>Note:</b> The ${quote.promo_discount.toFixed(2)} coupon discount stays with you — we only refund up to what the customer actually paid (${quote.total.toFixed(2)}).
                </div>
              )}
            </div>

            {canPomRefund && (
              <div className="mt-4 rounded-lg border border-[#FFD166]/40 bg-[#FFD166]/5 p-3 text-[11px] text-[#F6F1E4]" data-testid="pom-refund-callout">
                <div className="text-[#FFD166] font-medium mb-1">One-click Peace of Mind refund</div>
                Refund <b>${(quote.total - quote.enchanted_care_fee - (quote.already_refunded || 0)).toFixed(2)}</b> ({quote.total.toFixed(2)} paid − ${Number(quote.enchanted_care_fee || 0).toFixed(2)} Care{quote.already_refunded ? ` − $${Number(quote.already_refunded).toFixed(2)} prior` : ""}).
                <Btn onClick={submitPom} disabled={busy} className="mt-2 w-full" data-testid="pom-refund-submit">
                  ✨ {busy ? "Refunding…" : "Process Peace of Mind Refund"}
                </Btn>
              </div>
            )}

            <label className="block mt-5 text-sm">
              <span className="eyebrow">Refund amount ($) — override if needed</span>
              <input type="number" step="0.01" min="0" max={quote.max_refundable} value={amount} onChange={(e) => setAmount(e.target.value)} className="input mt-1" data-testid="cancel-refund-amount" />
              <div className="text-[11px] text-[#B9C8D5] mt-1">Max refundable: ${quote.max_refundable.toFixed(2)}</div>
            </label>

            <label className="block mt-4 text-sm">
              <span className="eyebrow">Why is this being cancelled?</span>
              <select
                value={reasonTag}
                onChange={(e) => setReasonTag(e.target.value)}
                className="input mt-1"
                data-testid="cancel-refund-reason-tag"
              >
                <option value="">— pick a reason (helps us tune copy) —</option>
                <option value="illness">Illness</option>
                <option value="weather">Weather</option>
                <option value="plans_changed">Plans changed</option>
                <option value="other">Other</option>
              </select>
            </label>

            {/* Reason-aware retention: "Plans changed" → offer a reschedule
                instead of a refund. Same delivery/pickup shift preserves the
                whole trip's revenue AND makes the customer feel taken care of. */}
            {reasonTag === "plans_changed" && (
              <div className="mt-4 rounded-xl border-2 border-[#FFD166]/50 bg-[#FFD166]/10 p-4" data-testid="retention-callout">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#FFD166] text-lg" aria-hidden>🌙</span>
                  <div className="font-serif text-lg text-[#FFD166]">Save this trip — reschedule instead?</div>
                </div>
                <p className="text-[12px] text-[#F6F1E4]/90 leading-snug mb-3">
                  Plans-changed customers reschedule at ~60 % when we ask. Pick their new dates below and we'll move the whole booking (no refund needed).
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    <span className="eyebrow text-[10px]">New delivery</span>
                    <input
                      type="date"
                      value={newDelivery}
                      onChange={(e) => setNewDelivery(e.target.value)}
                      className="input mt-1"
                      data-testid="retention-new-delivery"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="eyebrow text-[10px]">New pickup</span>
                    <input
                      type="date"
                      value={newPickup}
                      onChange={(e) => setNewPickup(e.target.value)}
                      className="input mt-1"
                      data-testid="retention-new-pickup"
                    />
                  </label>
                </div>
                <div className="text-[10px] text-[#B9C8D5] mt-2">
                  Original dates: {reservation.delivery_date} → {reservation.pickup_date}
                </div>
                <Btn
                  onClick={submitReschedule}
                  disabled={busy}
                  className="mt-3 w-full"
                  data-testid="retention-reschedule-submit"
                >
                  🌙 {busy ? "Rescheduling…" : `Reschedule (keep trip) → ${newDelivery} · ${newPickup}`}
                </Btn>
                <p className="text-[10px] text-[#B9C8D5] mt-2 text-center">
                  Prefer to refund anyway? Continue to the buttons below.
                </p>
              </div>
            )}

            <label className="block mt-4 text-sm">
              <span className="eyebrow">Note (optional — saved to history)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., weather, customer request, hurricane, etc." className="input mt-1 min-h-[70px]" data-testid="cancel-refund-note" />
            </label>

            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <Btn onClick={() => submit()} disabled={busy} data-testid="cancel-refund-submit">
                <XIcon className="w-4 h-4" /> {busy ? "Cancelling…" : `Cancel & refund $${Number(amount || 0).toFixed(2)}`}
              </Btn>
              <Btn variant="ghost" onClick={() => submit({ skipRefund: true })} disabled={busy} data-testid="cancel-refund-skip">
                Cancel without refund
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const MessageModal = ({ reservation, onClose }) => {
  const [subject, setSubject] = useState("A note about your Enchanted booking");
  const [body, setBody] = useState(`Hi ${reservation.customer_name},\n\n`);
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${reservation.id}/message`, { subject, body });
      if (data.sent) toast.success("Message sent"); else toast("Saved (email disabled — RESEND_API_KEY empty)");
      onClose();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="message-modal">
      <Card className="p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Message · {reservation.customer_name}</h3>
          <button onClick={onClose} className="text-[#B9C8D5]" data-testid="message-close"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-[#B9C8D5] mb-3">To: {reservation.customer_email}</div>
        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" data-testid="message-subject" />
        <textarea rows="7" value={body} onChange={e => setBody(e.target.value)} className="mt-3 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" data-testid="message-body" />
        <div className="mt-4 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={send} disabled={sending} data-testid="message-send"><Save className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send"}</Btn>
        </div>
      </Card>
    </div>
  );
};


/* ------------ Extra charge modal ------------ */
const REFUND_REASON_TAGS = [
  { id: "late_delivery", label: "Late delivery" },
  { id: "damaged_item", label: "Damaged item" },
  { id: "item_missing", label: "Item missing" },
  { id: "service_comp", label: "Customer service comp" },
  { id: "duplicate_charge", label: "Duplicate charge" },
  { id: "plans_changed", label: "Plans changed" },
  { id: "other", label: "Other" },
];

const ManualRefundModal = ({ reservation: r, onClose, onDone }) => {
  const total = Number(r.total || 0);
  const alreadyRefunded = Number(r.admin_refund_total || 0);
  const remaining = Math.max(0, +(total - alreadyRefunded).toFixed(2));
  const [amount, setAmount] = React.useState("");
  const [reasonTag, setReasonTag] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("Enter an amount greater than $0"); return; }
    if (amt > remaining + 0.001) { toast.error(`Max refundable is $${remaining.toFixed(2)}.`); return; }
    if (!reasonTag) { toast.error("Pick a refund category so you can filter reports later."); return; }
    if (!reason.trim()) { toast.error("Add a note so you remember why you refunded this later."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/partial-refund`, {
        amount: amt, reason: reason.trim(), reason_tag: reasonTag,
      });
      if (data.duplicate) {
        toast.warning("That exact refund amount was already processed — no double-refund issued.");
      } else {
        toast.success(`Refunded $${Number(data.refunded).toFixed(2)} ✓`);
      }
      onDone && onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="manual-refund-modal">
      <div className="glass max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="manual-refund-close"><XIcon className="w-5 h-5" /></button>
        <h2 className="font-serif text-2xl text-[#FFD166]">Manual refund · {r.customer_name}</h2>
        <p className="text-xs text-[#B9C8D5] mb-5">Refunds any amount straight to the original card. Reservation stays active. The note is saved to the activity timeline so you can see later why you issued the refund.</p>

        <div className="grid grid-cols-3 gap-3 text-xs mb-4">
          <div className="rounded-lg border border-[#FFD166]/20 bg-black/25 p-3">
            <div className="eyebrow mb-1">Total</div>
            <div className="text-sm text-[#F6F1E4]">${total.toFixed(2)}</div>
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="eyebrow mb-1">Refunded so far</div>
            <div className="text-sm text-amber-200">${alreadyRefunded.toFixed(2)}</div>
          </div>
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div className="eyebrow mb-1">Refundable</div>
            <div className="text-sm text-emerald-200" data-testid="manual-refund-max">${remaining.toFixed(2)}</div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="eyebrow">Refund amount</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FFD166]">$</span>
              <Input type="number" step="0.01" min="0.50" max={remaining} value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-6" placeholder="0.00" data-testid="manual-refund-amount" />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <button type="button" onClick={() => setAmount(remaining.toFixed(2))} className="text-[10px] px-2 py-1 rounded-full border border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10" data-testid="manual-refund-fill-max">Refund all (${remaining.toFixed(2)})</button>
              {[10, 25, 50].map((n) => (
                n <= remaining ? (
                  <button key={n} type="button" onClick={() => setAmount(n.toFixed(2))} className="text-[10px] px-2 py-1 rounded-full border border-[#FFD166]/20 text-[#B9C8D5] hover:text-[#FFD166] hover:border-[#FFD166]/50">${n}</button>
                ) : null
              ))}
            </div>
          </div>
          <div>
            <label className="eyebrow">Category (for reports)</label>
            <select
              value={reasonTag}
              onChange={(e) => setReasonTag(e.target.value)}
              className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg mt-1"
              data-testid="manual-refund-tag"
            >
              <option value="">Choose a reason…</option>
              {REFUND_REASON_TAGS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow">Note (saved to activity timeline)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={250}
              className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg min-h-[80px] mt-1"
              placeholder="e.g. Comped one night after late delivery on Friday."
              data-testid="manual-refund-reason"
            />
            <div className="text-[10px] text-[#B9C8D5]/70 text-right mt-1">{reason.length}/250</div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 justify-end">
          <Btn variant="ghost" onClick={onClose} data-testid="manual-refund-cancel">Cancel</Btn>
          <Btn onClick={submit} disabled={busy || !amount || !reasonTag || !reason.trim()} data-testid="manual-refund-submit">
            {busy ? "Refunding…" : `Refund${amount ? ` $${Number(amount).toFixed(2)}` : ""}`}
          </Btn>
        </div>
      </div>
    </div>
  );
};

const ApplyPromoCodeModal = ({ reservation: r, onClose, onDone }) => {
  const [code, setCode] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!code.trim()) { toast.error("Enter a promo code."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/apply-promo-code`, {
        code: code.trim().toUpperCase(), note: note.trim(),
      });
      toast.success(`Applied ${data.code} · ${data.discount_percent}% off · $${Number(data.refunded).toFixed(2)} refunded ✓`);
      onDone && onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't apply promo code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="apply-promo-modal">
      <div className="glass max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="apply-promo-close"><XIcon className="w-5 h-5" /></button>
        <h2 className="font-serif text-2xl text-[#FFD166]">Apply promo code · {r.customer_name}</h2>
        <p className="text-xs text-[#B9C8D5] mb-5">Retroactively applies a Firefly Wishes promo code. The percent discount is refunded to the customer's original card, the code is stamped onto the reservation, and the code itself is marked used.</p>

        <div className="space-y-3">
          <div>
            <label className="eyebrow">Promo code</label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="FIREFLY42" className="mt-1 font-mono uppercase" data-testid="apply-promo-code" />
          </div>
          <div>
            <label className="eyebrow">Note (optional — saved to activity timeline)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={250}
              className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg min-h-[70px] mt-1"
              placeholder="e.g. Comped after packaging mishap"
              data-testid="apply-promo-note"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 justify-end">
          <Btn variant="ghost" onClick={onClose} data-testid="apply-promo-cancel">Cancel</Btn>
          <Btn onClick={submit} disabled={busy || !code.trim()} data-testid="apply-promo-submit">
            {busy ? "Applying…" : "Apply & refund"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

const ExtraChargeModal = ({ reservation: r, onClose, onDone }) => {
  const [amount, setAmount] = React.useState(r._prefill_amount ? String(r._prefill_amount) : "");
  const [description, setDescription] = React.useState(r._prefill_desc || "");
  const [busy, setBusy] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(true);
  const [sendSms, setSendSms] = React.useState(true);
  const [result, setResult] = React.useState(null);
  const [confirmSilent, setConfirmSilent] = React.useState(false);
  const hasSavedCard = !!(r.card_last4 && r.stripe_payment_method_id);

  const submit = async (method) => {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("Amount must be greater than $0"); return; }
    if (!description.trim()) { toast.error("Add a description — the customer sees it."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/extra-charge`, {
        amount: amt, description: description.trim(), method,
        send_email: sendEmail, send_sms: sendSms,
      });
      if (method === "saved_card") {
        if (data.ok === false) {
          if (data.requires_action) {
            toast.warning("Bank asked for 3DS — sending pay-link instead.");
            // Fall back to pay-link automatically.
            const link = await axios.post(`${API}/admin/reservations/${r.id}/extra-charge`, {
              amount: amt, description: description.trim(), method: "pay_link",
              send_email: sendEmail, send_sms: sendSms,
            });
            setResult(link.data);
          } else {
            toast.error(data.error_message || "Card was declined.");
          }
        } else {
          setResult(data);
          toast.success(`Charged $${amt.toFixed(2)} to ${(data.card_brand || "card").toUpperCase()} ••••${data.card_last4} ✓`);
        }
      } else {
        setResult(data);
        toast.success("Payment link sent ✨");
      }
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirmSilent(false); }
  };
  const copyLink = () => { navigator.clipboard?.writeText(result?.pay_url || ""); toast.success("Link copied"); };
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="extra-charge-modal">
      <div className="glass max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="extra-charge-close"><XIcon className="w-5 h-5" /></button>
        <h2 className="font-serif text-2xl text-[#FFD166]">Extra charge · {r.customer_name}</h2>
        <p className="text-xs text-[#B9C8D5] mb-5">Send the customer a pay-link for damage, cleaning, or an add-on. They tap and pay — you stay hands-off.</p>
        {!result ? (
          <>
            <div className="space-y-3">
              <div>
                <label className="eyebrow">Amount</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FFD166]">$</span>
                  <Input type="number" step="0.01" min="0.50" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-6" placeholder="0.00" data-testid="extra-amount" />
                </div>
              </div>
              <div>
                <label className="eyebrow">What's the charge for? (customer sees this)</label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Excessive cleaning for pack-n-play" className="mt-1" data-testid="extra-description" />
              </div>
            </div>

            {hasSavedCard && (
              <div className="mt-5 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="extra-saved-card-panel">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-emerald-300" />
                  <span className="text-sm text-[#F6F1E4]">Card on file · <b className="text-emerald-200">{(r.card_brand || "card").toUpperCase()} ••••{r.card_last4}</b></span>
                </div>
                <Btn onClick={() => setConfirmSilent(true)} disabled={busy || !amount || !description.trim()} className="w-full" data-testid="extra-charge-silent-btn">
                  <CreditCard className="w-4 h-4" /> Charge card silently{amount ? ` · $${Number(amount).toFixed(2)}` : ""}
                </Btn>
                <p className="text-[10px] text-[#B9C8D5] mt-2 italic">Uses off-session payments. If the bank asks for 3DS, we'll fall back to a pay-link automatically.</p>
              </div>
            )}

            <div className="mt-5">
              <div className="eyebrow mb-2">Or send a pay-link</div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-[#FFD166]" data-testid="extra-send-email" /> Email</label>
                <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="accent-[#FFD166]" data-testid="extra-send-sms" /> SMS</label>
              </div>
              <div className="flex gap-2 justify-end">
                <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn onClick={() => submit("pay_link")} disabled={busy} data-testid="extra-submit"><Save className="w-4 h-4" /> {busy ? "Sending…" : "Send pay-link"}</Btn>
              </div>
            </div>
          </>
        ) : (
          <div>
            {result.silent ? (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-1 text-sm" data-testid="extra-silent-result">
                <div className="text-emerald-300 font-medium">✓ Card charged successfully</div>
                <div>${Number(result.amount).toFixed(2)} · {(result.card_brand || "card").toUpperCase()} ••••{result.card_last4}</div>
                <div className="text-[10px] text-[#B9C8D5]">Status: {result.status}</div>
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-1 text-sm">
                <div className="text-emerald-300">✓ Payment link created</div>
                <div>Email {result.email_sent ? "sent ✓" : "skipped"} · SMS {result.sms_sent ? "sent ✓" : "skipped"}</div>
              </div>
            )}
            {result.pay_url && (
              <div className="mt-4 flex gap-2 items-center">
                <div className="flex-1 text-[10px] font-mono bg-black/40 border border-[#FFD166]/20 rounded-lg px-3 py-2 truncate">{result.pay_url}</div>
                <Btn variant="ghost" onClick={copyLink} data-testid="extra-copy-link">Copy</Btn>
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Btn onClick={async () => {
                if (r._notify_reschedule) {
                  try {
                    const d = await axios.post(`${API}/admin/reservations/${r.id}/notify-reschedule`, {
                      new_delivery_date: r._new_delivery_date,
                      new_pickup_date: r._new_pickup_date,
                    });
                    if (d.data?.sent) toast.success("Customer notified of date change ✓");
                  } catch (_) {}
                }
                onDone();
              }} data-testid="extra-done">Done</Btn>
            </div>
          </div>
        )}
      </div>

      {confirmSilent && (
        <div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmSilent(false)} data-testid="silent-charge-confirm">
          <div className="glass max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-[#FFD166] mb-2">Charge card?</h3>
            <p className="text-sm text-[#F6F1E4] mb-4">
              You're about to charge <b className="text-emerald-300">${Number(amount || 0).toFixed(2)}</b> to <b>{(r.card_brand || "card").toUpperCase()} ••••{r.card_last4}</b> for:
            </p>
            <p className="text-sm text-[#B9C8D5] italic mb-5 p-3 bg-black/40 rounded border border-[#FFD166]/20">"{description}"</p>
            <p className="text-[11px] text-[#F4A93A] mb-5">The customer won't need to tap anything — they'll get an emailed receipt.</p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setConfirmSilent(false)} disabled={busy}>Cancel</Btn>
              <Btn onClick={() => submit("saved_card")} disabled={busy} data-testid="silent-charge-confirm-btn"><CreditCard className="w-4 h-4" /> {busy ? "Charging…" : `Charge $${Number(amount || 0).toFixed(2)}`}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------ Edit reservation modal ------------ */
const LOCATION_LABELS_DELIVERY = {
  disneyPlaceholder: "Select Disney Resort…",
  areaPlaceholder: "Select area…",
  addressPlaceholder: "Enter delivery address…",
  offsiteResortPlaceholder: "Select property…",
  successAddress: "We deliver here!",
  failAddress: "We don't deliver there",
};
const LOCATION_LABELS_PICKUP = {
  disneyPlaceholder: "Select Disney Resort…",
  areaPlaceholder: "Select area…",
  addressPlaceholder: "Enter pickup address…",
  offsiteResortPlaceholder: "Select property…",
  successAddress: "We pick up from here!",
  failAddress: "We don't pick up there",
};

const EditReservationModal = ({ reservation: r, products, onClose, onDone }) => {
  const startCart = {};
  (r.line_items || []).forEach((li) => { startCart[li.product_id] = li.quantity; });
  const [cart, setCart] = React.useState(startCart);
  const [deliveryDate, setDeliveryDate] = React.useState(r.delivery_date);
  const [pickupDate, setPickupDate] = React.useState(r.pickup_date);
  const [dp, setDp] = React.useState(!!r.damage_protection);
  const [editNote, setEditNote] = React.useState("");
  // reviewData: null = form step, {new_quote,already_paid,delta} = review step
  const [reviewData, setReviewData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [refunding, setRefunding] = React.useState(false);

  // Time slots
  const [timeSlots, setTimeSlots] = React.useState([]);
  const [deliverySlot, setDeliverySlot] = React.useState(r.delivery_slot || "");
  const [pickupSlot, setPickupSlot] = React.useState(r.pickup_slot || "");

  // Zones for LocationPicker
  const [zones, setZones] = React.useState([]);

  const DISNEY_ZONE_IDS = ["wdw-core"];
  const initDelivIsOff = r.zone_id
    ? !DISNEY_ZONE_IDS.includes(r.zone_id)
    : (!r.delivery_resort && !!r.delivery_address);
  const [delivLoc, setDelivLoc] = React.useState({
    isOffProperty: initDelivIsOff,
    zoneId: r.zone_id || "",
    resort: r.delivery_resort || "",
    address: r.delivery_address || "",
    addrCheck: null,
  });

  const initSamePickup = !r.pickup_resort || r.pickup_resort === r.delivery_resort;
  const [samePickup, setSamePickup] = React.useState(initSamePickup);
  const initPickIsOff = r.pickup_zone_id
    ? !DISNEY_ZONE_IDS.includes(r.pickup_zone_id)
    : (!r.pickup_resort && !!r.pickup_address);
  const [pickLoc, setPickLoc] = React.useState({
    isOffProperty: initPickIsOff,
    zoneId: r.pickup_zone_id || r.zone_id || "",
    resort: r.pickup_resort || "",
    address: r.pickup_address || "",
    addrCheck: null,
  });

  React.useEffect(() => {
    axios.get(`${API}/zones`).then((res) => setZones(res.data.zones || [])).catch(() => {});
    axios.get(`${API}/time-slots`).then((res) => setTimeSlots(res.data.slots || [])).catch(() => {});
  }, []);

  const setDelivLocField = (field) => (val) => setDelivLoc((s) => ({ ...s, [field]: val }));
  const setPickLocField  = (field) => (val) => setPickLoc((s) => ({ ...s, [field]: val }));
  const slotOptions = timeSlots.map((s) => ({ value: s.id, label: s.label }));

  // Build the location payload (shared by preview and save)
  const buildPayload = () => {
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity }));
    const delivLocation = delivLoc.isOffProperty
      ? { zone_id: delivLoc.zoneId, delivery_resort: delivLoc.resort, delivery_address: delivLoc.address }
      : { zone_id: delivLoc.zoneId, delivery_resort: delivLoc.resort, delivery_address: "" };
    const pickLocation = samePickup
      ? { pickup_zone_id: delivLoc.zoneId, pickup_resort: delivLoc.resort, pickup_address: delivLoc.address }
      : pickLoc.isOffProperty
        ? { pickup_zone_id: pickLoc.zoneId, pickup_resort: pickLoc.resort, pickup_address: pickLoc.address }
        : { pickup_zone_id: pickLoc.zoneId, pickup_resort: pickLoc.resort, pickup_address: "" };
    return { items, delivery_date: deliveryDate, pickup_date: pickupDate, damage_protection: dp,
      delivery_slot: deliverySlot || undefined, pickup_slot: pickupSlot || undefined,
      edit_note: editNote.trim() || undefined,
      ...delivLocation, ...pickLocation };
  };

  // Compute field-level diff for the review panel
  const computeChanges = () => {
    const changes = [];
    const fmt = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
    const slotLabel = (id) => timeSlots.find((s) => s.id === id)?.label || id || "None";
    const locStr = (resort, address) => resort || address || "—";

    if (deliveryDate !== r.delivery_date)
      changes.push({ field: "Delivery date", old: fmt(r.delivery_date), next: fmt(deliveryDate) });
    if (pickupDate !== r.pickup_date)
      changes.push({ field: "Pickup date", old: fmt(r.pickup_date), next: fmt(pickupDate) });
    if ((deliverySlot || "") !== (r.delivery_slot || ""))
      changes.push({ field: "Delivery window", old: slotLabel(r.delivery_slot), next: slotLabel(deliverySlot) });
    if ((pickupSlot || "") !== (r.pickup_slot || ""))
      changes.push({ field: "Pickup window", old: slotLabel(r.pickup_slot), next: slotLabel(pickupSlot) });

    const oldDelivLoc = locStr(r.delivery_resort, r.delivery_address);
    const newDelivLoc = locStr(delivLoc.resort, delivLoc.address);
    if (oldDelivLoc !== newDelivLoc)
      changes.push({ field: "Delivery location", old: oldDelivLoc, next: newDelivLoc });

    const oldPickupLoc = locStr(r.pickup_resort, r.pickup_address);
    const effPickup = samePickup ? { resort: delivLoc.resort, address: delivLoc.address } : pickLoc;
    const newPickupLoc = locStr(effPickup.resort, effPickup.address);
    if (oldPickupLoc !== newPickupLoc)
      changes.push({ field: "Pickup location", old: oldPickupLoc, next: newPickupLoc });

    if (!!dp !== !!r.damage_protection)
      changes.push({ field: "Damage Protection", old: r.damage_protection ? "On ($20)" : "Off", next: dp ? "On ($20)" : "Off" });

    // Items diff
    const oldQty = {};
    (r.line_items || []).forEach((li) => { oldQty[li.product_id] = li.quantity; });
    const allIds = new Set([...Object.keys(oldQty), ...Object.keys(cart)]);
    allIds.forEach((pid) => {
      const oq = oldQty[pid] || 0;
      const nq = cart[pid] || 0;
      if (oq !== nq) {
        const name = products.find((p) => p.id === pid)?.name || pid;
        const tag = oq === 0 ? " (Added)" : nq === 0 ? " (Removed)" : "";
        changes.push({ field: name, old: oq === 0 ? "—" : `Qty ${oq}`, next: nq === 0 ? "Removed" : `Qty ${nq}${tag}` });
      }
    });
    return changes;
  };

  // Step 1 — Review: call /edit/preview (no DB save), build changes list, show review panel
  const reviewChange = async () => {
    const payload = buildPayload();
    if (payload.items.length === 0) { toast.error("At least one item required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/edit/preview`, payload);
      setReviewData({ ...data, changes: computeChanges() });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Preview failed"); }
    finally { setBusy(false); }
  };

  // Step 2 — Confirm: call /edit (actual DB save)
  const confirmSave = async () => {
    const payload = buildPayload();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/edit`, payload);
      toast.success(
        data.delta > 0 ? `Booking updated — customer owes $${data.delta.toFixed(2)} extra`
        : data.delta < 0 ? `Booking updated — overpayment of $${(-data.delta).toFixed(2)}`
        : "Booking updated — no charge change"
      );
      // Merge saved delta into reviewData so action buttons render correctly
      setReviewData((prev) => ({ ...prev, ...data, confirmed: true }));
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const doRefund = async () => {
    if (!reviewData || reviewData.delta >= 0) return;
    const amount = Math.round(Math.abs(reviewData.delta) * 100) / 100;
    if (!window.confirm(`Refund $${amount.toFixed(2)} back to the customer's card?`)) return;
    setRefunding(true);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/partial-refund`, {
        amount, reason: "Booking edit — dates/items changed, overpayment refunded",
      });
      toast.success(`Refunded $${data.refunded.toFixed(2)} to the customer ✓`);
      onDone(0, { deliveryDate, pickupDate });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Refund failed"); }
    finally { setRefunding(false); }
  };

  const updateQty = (id, delta) => setCart((c) => {
    const next = { ...c, [id]: Math.max(0, (c[id] || 0) + delta) };
    if (next[id] === 0) delete next[id];
    return next;
  });

  // ── REVIEW PANEL ────────────────────────────────────────────────────────────
  if (reviewData) {
    const { changes, delta, already_paid, new_quote, confirmed } = reviewData;
    return (
      <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-reservation-modal">
        <div className="glass max-w-2xl w-full p-6 sm:p-8 my-8 relative" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="edit-close"><XIcon className="w-5 h-5" /></button>

          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/40 flex items-center justify-center">
              <Check className="w-4 h-4 text-[#FFD166]" />
            </div>
            <h2 className="font-serif text-2xl text-[#FFD166]">Review Changes</h2>
          </div>
          <p className="text-xs text-[#B9C8D5] mb-6 ml-11">{r.customer_name} · Order #{r.id?.slice(0, 8).toUpperCase()}</p>

          {/* Changed fields */}
          {changes.length === 0 ? (
            <div className="rounded-lg border border-[#FFD166]/20 bg-black/30 p-4 mb-5 text-sm text-[#B9C8D5] text-center" data-testid="edit-no-changes">
              No fields were changed.
            </div>
          ) : (
            <div className="rounded-lg border border-[#FFD166]/20 bg-black/20 overflow-hidden mb-5" data-testid="edit-changes-list">
              <div className="grid grid-cols-3 gap-2 px-4 py-2 bg-black/40 border-b border-[#FFD166]/10">
                <span className="eyebrow text-[10px]">Field</span>
                <span className="eyebrow text-[10px]">Before</span>
                <span className="eyebrow text-[10px]">After</span>
              </div>
              {changes.map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 px-4 py-3 border-b border-[#FFD166]/5 last:border-0 hover:bg-white/[0.02]">
                  <span className="text-xs text-[#B9C8D5] font-medium">{c.field}</span>
                  <span className="text-xs text-[#F6F1E4]/60 line-through">{c.old}</span>
                  <span className="text-xs text-[#FFD166] font-semibold">{c.next}</span>
                </div>
              ))}
            </div>
          )}

          {/* Price summary */}
          <div className="rounded-lg border border-[#FFD166]/30 bg-[#FFD166]/5 p-4 mb-4 space-y-1 text-sm" data-testid="edit-price-summary">
            <div className="flex justify-between text-[#B9C8D5]">
              <span>Customer paid</span><span>${already_paid.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[#F6F1E4]">
              <span>New total</span><span>${new_quote.total.toFixed(2)}</span>
            </div>
            <div className={`flex justify-between font-serif text-lg pt-2 border-t border-[#FFD166]/15 ${delta > 0 ? "text-[#FFD166]" : delta < 0 ? "text-emerald-300" : "text-[#B9C8D5]"}`}>
              <span>{delta > 0 ? "Additional owed by customer" : delta < 0 ? "Refund due to customer" : "No charge change"}</span>
              <span>{delta !== 0 ? `$${Math.abs(delta).toFixed(2)}` : "—"}</span>
            </div>
            {delta === 0 && (
              <p className="text-xs text-[#B9C8D5]/70 pt-1">Customer will automatically receive an updated reservation confirmation.</p>
            )}
          </div>

          {/* Internal note summary (if provided) */}
          {editNote.trim() && (
            <div className="rounded-lg border border-[#FFD166]/15 bg-black/30 px-4 py-3 mb-6 flex gap-3 items-start" data-testid="edit-note-preview">
              <span className="eyebrow text-[10px] mt-0.5 shrink-0">Note</span>
              <span className="text-sm text-[#B9C8D5] italic">"{editNote.trim()}"</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between items-center gap-2">
            {!confirmed ? (
              <>
                <Btn variant="ghost" onClick={() => setReviewData(null)} disabled={busy} data-testid="edit-back-btn">
                  ← Back to edit
                </Btn>
                <Btn onClick={confirmSave} disabled={busy || changes.length === 0} data-testid="edit-confirm-btn">
                  <Check className="w-4 h-4" /> {busy ? "Saving…" : "Confirm changes"}
                </Btn>
              </>
            ) : delta > 0 ? (
              <>
                <span className="text-xs text-[#B9C8D5]">Saved — now collect the extra charge</span>
                <Btn onClick={() => onDone(delta, { deliveryDate, pickupDate })} data-testid="edit-done-btn">
                  Charge ${delta.toFixed(2)} extra →
                </Btn>
              </>
            ) : delta < 0 ? (
              <>
                <span className="text-xs text-[#B9C8D5]">Saved — issue the refund</span>
                <Btn onClick={doRefund} disabled={refunding} data-testid="edit-refund-btn">
                  {refunding ? "Refunding…" : `Refund $${Math.abs(delta).toFixed(2)}`}
                </Btn>
              </>
            ) : (
              <>
                <span className="text-xs text-emerald-400">Changes saved — customer notified</span>
                <Btn onClick={() => onDone(0, { deliveryDate, pickupDate })} data-testid="edit-done-btn">
                  <Check className="w-4 h-4" /> Done
                </Btn>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── EDIT FORM ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="edit-reservation-modal">
      <div className="glass max-w-2xl w-full p-6 sm:p-8 my-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166]" data-testid="edit-close"><XIcon className="w-5 h-5" /></button>
        <h2 className="font-serif text-2xl text-[#FFD166]">Edit booking · {r.customer_name}</h2>
        <p className="text-xs text-[#B9C8D5] mb-5">Adjust dates, time windows, location, items, or Damage Protection. Hit <strong>Review Change</strong> to see a summary before saving.</p>

        {/* Dates */}
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="eyebrow">Delivery date</label>
            <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1" data-testid="edit-delivery-date" />
          </div>
          <div>
            <label className="eyebrow">Pickup date</label>
            <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className="mt-1" data-testid="edit-pickup-date" />
          </div>
        </div>

        {/* Time windows */}
        <div className="grid md:grid-cols-2 gap-3 mb-5">
          <div>
            <label className="eyebrow">Delivery window</label>
            <ThemedSelect value={deliverySlot} onChange={setDeliverySlot} options={slotOptions} placeholder="Select window…" className="mt-1" data-testid="edit-delivery-slot" />
          </div>
          <div>
            <label className="eyebrow">Pickup window</label>
            <ThemedSelect value={pickupSlot} onChange={setPickupSlot} options={slotOptions} placeholder="Select window…" className="mt-1" data-testid="edit-pickup-slot" />
          </div>
        </div>

        {/* Delivery location */}
        <div className="mb-5">
          <div className="eyebrow mb-2">Delivery location</div>
          <LocationPicker
            state={delivLoc}
            setIsOffProperty={setDelivLocField("isOffProperty")}
            setZoneId={setDelivLocField("zoneId")}
            setResort={setDelivLocField("resort")}
            setAddress={setDelivLocField("address")}
            setAddrCheck={setDelivLocField("addrCheck")}
            zones={zones}
            testidPrefix="edit-delivery"
            labels={LOCATION_LABELS_DELIVERY}
            verb="deliver"
          />
        </div>

        {/* Pickup location */}
        <div className="mb-5">
          <label className="flex items-center gap-2 cursor-pointer mb-3 p-2 rounded border border-[#FFD166]/15 bg-black/20">
            <input type="checkbox" checked={samePickup} onChange={(e) => setSamePickup(e.target.checked)} className="accent-[#FFD166]" data-testid="edit-same-pickup" />
            <span className="text-sm text-[#B9C8D5]">Pickup at same location as delivery</span>
          </label>
          {!samePickup && (
            <>
              <div className="eyebrow mb-2">Pickup location</div>
              <LocationPicker
                state={pickLoc}
                setIsOffProperty={setPickLocField("isOffProperty")}
                setZoneId={setPickLocField("zoneId")}
                setResort={setPickLocField("resort")}
                setAddress={setPickLocField("address")}
                setAddrCheck={setPickLocField("addrCheck")}
                zones={zones}
                testidPrefix="edit-pickup"
                labels={LOCATION_LABELS_PICKUP}
                verb="pick up"
              />
            </>
          )}
        </div>

        {/* Damage Protection */}
        <label className="flex items-start gap-2 rounded-lg border border-[#FFD166]/20 bg-black/30 p-3 cursor-pointer mb-5">
          <input type="checkbox" checked={dp} onChange={(e) => setDp(e.target.checked)} className="mt-0.5 accent-[#FFD166]" data-testid="edit-dp" />
          <span className="text-sm">$20 Damage Protection</span>
        </label>

        {/* Items */}
        <div className="mb-6">
          <div className="eyebrow mb-2">Items</div>
          <div className="max-h-[260px] overflow-y-auto space-y-1 border border-[#FFD166]/10 rounded-lg p-2" data-testid="edit-item-list">
            {products.filter(p => p.active !== false).map((p) => (
              <div key={p.id} className="flex items-center gap-2 p-2 rounded hover:bg-black/30">
                <span className="flex-1 text-sm">{p.name}</span>
                <span className="text-xs text-[#B9C8D5] w-16 text-right">${p.price_per_day}/d</span>
                <button type="button" onClick={() => updateQty(p.id, -1)} className="w-6 h-6 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`edit-dec-${p.id}`}>−</button>
                <span className="w-6 text-center text-sm" data-testid={`edit-qty-${p.id}`}>{cart[p.id] || 0}</span>
                <button type="button" onClick={() => updateQty(p.id, +1)} className="w-6 h-6 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`edit-inc-${p.id}`}>+</button>
              </div>
            ))}
          </div>
        </div>

        {/* Internal note for this change */}
        <div className="mb-6">
          <label className="eyebrow">Reason for change <span className="text-[#B9C8D5]/50 normal-case">(internal — saved to activity timeline)</span></label>
          <textarea
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            placeholder="e.g. Customer called to move dates, resort availability issue…"
            rows={2}
            className="mt-1 w-full bg-black/30 border border-[#FFD166]/20 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm resize-none placeholder:text-[#B9C8D5]/40 focus:outline-none focus:border-[#FFD166]/50"
            data-testid="edit-change-note"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn onClick={reviewChange} disabled={busy} data-testid="edit-recalc-btn">
            <Search className="w-4 h-4" /> {busy ? "Checking…" : "Review change"}
          </Btn>
        </div>
      </div>
    </div>
  );
};